// core/src/vue/useChatStream.ts
import { computed, nextTick, ref, shallowRef, watch } from "vue";

// core/src/ai/adapters.ts
function createMemoryStorage(seed = {}) {
  const map = new Map(Object.entries(seed));
  return {
    get: (k) => map.has(k) ? map.get(k) : null,
    set: (k, v) => void map.set(k, v),
    remove: (k) => void map.delete(k)
  };
}
var noopNotifier = {};
function createAbortController() {
  const Native = globalThis.AbortController;
  if (typeof Native === "function") {
    let native = null;
    try {
      native = new Native();
    } catch {
      native = null;
    }
    if (native && native.signal) {
      return {
        get signal() {
          return native.signal;
        },
        abort(reason) {
          native.abort(reason);
        }
      };
    }
  }
  let aborted = false;
  const listeners = /* @__PURE__ */ new Set();
  const signal = {
    get aborted() {
      return aborted;
    },
    addEventListener: (_type, listener) => void listeners.add(listener),
    removeEventListener: (_type, listener) => void listeners.delete(listener)
  };
  return {
    signal,
    abort() {
      if (aborted) return;
      aborted = true;
      for (const l of listeners) {
        try {
          l();
        } catch {
        }
      }
      listeners.clear();
    }
  };
}

// core/src/ai/render.ts
function currentTime() {
  const d = /* @__PURE__ */ new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}
function truncate(text, len) {
  if (!text) return "";
  return text.length > len ? text.slice(0, len) + "..." : text;
}
function formatToolArguments(args) {
  if (!args) return "";
  if (typeof args === "object") {
    try {
      return JSON.stringify(args, null, 2);
    } catch {
      return String(args);
    }
  }
  const raw = String(args);
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}
function formatToolResult(result) {
  if (!result) return "";
  if (typeof result === "object") {
    try {
      return JSON.stringify(result, null, 2);
    } catch {
      return String(result);
    }
  }
  const raw = String(result);
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}
function escapeHtml(str) {
  if (!str) return "";
  return str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
function isAgentInvokeNode(node) {
  return !!(node && typeof node.toolName === "string" && node.toolName.startsWith("agent_invoke_"));
}
function isWebSearchTool(toolName) {
  if (!toolName) return false;
  const name = toolName.toLowerCase();
  return name === "websearch" || name === "\u4E92\u8054\u7F51\u641C\u7D22" || name === "internet_search";
}
function isReportTool(toolName) {
  if (!toolName) return false;
  const name = toolName.toLowerCase();
  return name === "report" || name === "\u62A5\u544A\u751F\u6210" || name === "\u62A5\u544A";
}
function getReportCards(msg) {
  if (!msg || !msg.toolCalls) return [];
  const cards = [];
  for (const tc of msg.toolCalls) {
    if (!isReportTool(tc.toolName) || !tc.result) continue;
    try {
      const parsed = JSON.parse(String(tc.result));
      if (parsed && parsed.type === "report") cards.push(parsed);
    } catch {
    }
  }
  return cards;
}
function getWebSearchQuery(msg) {
  if (!msg) return "";
  if (msg.optimizedSearchQuery) return String(msg.optimizedSearchQuery);
  if (!msg.toolCalls) return "";
  const tc = msg.toolCalls.find((t) => isWebSearchTool(t.toolName));
  if (!tc || !tc.arguments) return "";
  try {
    const args = typeof tc.arguments === "string" ? JSON.parse(tc.arguments) : tc.arguments;
    return args.query || "";
  } catch {
    return String(tc.arguments);
  }
}
function hasNonWebSearchTools(msg) {
  if (!msg || !msg.toolCalls || msg.toolCalls.length === 0) return false;
  return msg.toolCalls.some((tc) => !isWebSearchTool(tc.toolName) && !isReportTool(tc.toolName));
}
function isWebSearchExpanded(msg) {
  return !!(msg && msg._webSearchExpanded);
}
function parseWebSearchResults(toolCalls) {
  if (!toolCalls || toolCalls.length === 0) return { results: [], optimizedQuery: "" };
  const results = [];
  let optimizedQuery = "";
  for (const tc of toolCalls) {
    if (!isWebSearchTool(tc.toolName) || !tc.result) continue;
    try {
      const parsed = JSON.parse(String(tc.result));
      if (parsed.optimized_query) optimizedQuery = parsed.optimized_query;
      if (parsed.success && Array.isArray(parsed.results)) {
        for (const item of parsed.results) {
          if (item.title || item.url) {
            results.push({ title: item.title || "", url: item.url || "", snippet: item.snippet || "" });
          }
        }
      }
    } catch {
    }
  }
  return { results, optimizedQuery };
}
var TOOL_NAME_MAP = {
  device_data_query: "\u67E5\u8BE2\u8BBE\u5907\u6570\u636E",
  device_query: "\u67E5\u8BE2\u8BBE\u5907",
  knowledge_search: "\u68C0\u7D22\u77E5\u8BC6\u5E93",
  websearch: "\u641C\u7D22\u4E92\u8054\u7F51",
  device_status: "\u67E5\u8BE2\u8BBE\u5907\u72B6\u6001",
  device_command_list: "\u67E5\u8BE2\u8BBE\u5907\u6307\u4EE4",
  device_command_send: "\u4E0B\u53D1\u8BBE\u5907\u6307\u4EE4",
  place_query: "\u67E5\u8BE2\u573A\u5730\uFF08\u5858\u53E3/\u5E93\u623F\uFF09",
  feed_record_query: "\u67E5\u8BE2\u6295\u5582\u8BB0\u5F55",
  skill_script_exec: "\u6267\u884C\u6280\u80FD\u811A\u672C",
  user_create: "\u521B\u5EFA\u7528\u6237",
  scene_create: "\u521B\u5EFA\u573A\u666F\u8054\u52A8",
  device_create: "\u521B\u5EFA\u8BBE\u5907",
  create_project: "\u521B\u5EFA\u9879\u76EE",
  // 2026-11-04 补全（ai_agent_tool 在册工具全量，调用过程不露英文）
  device_history_data_query: "\u67E5\u8BE2\u8BBE\u5907\u5386\u53F2\u6570\u636E",
  do_prediction: "\u6267\u884C\u9884\u6D4B\u5206\u6790",
  skill_asset_read: "\u8BFB\u53D6\u6280\u80FD\u8D44\u4EA7",
  activity_create: "\u521B\u5EFA\u6D3B\u52A8",
  plan_query: "\u67E5\u8BE2\u8BA1\u5212",
  growth_create: "\u521B\u5EFA\u6210\u957F\u8BB0\u5F55",
  input_goods_create: "\u767B\u8BB0\u6295\u5165\u54C1",
  crab_sign_publish: "\u53D1\u5E03\u87F9\u7801",
  sale_compliance_check: "\u9500\u552E\u5408\u89C4\u68C0\u67E5",
  cert_material_generate: "\u751F\u6210\u8BA4\u8BC1\u6750\u6599",
  dept_role_query: "\u67E5\u8BE2\u90E8\u95E8\u4E0E\u89D2\u8272",
  dataroom_designer: "\u8BBE\u8BA1\u6570\u636E\u95F4",
  "dataroom-designer": "\u8BBE\u8BA1\u6570\u636E\u95F4",
  report: "\u751F\u6210\u62A5\u544A"
};
var AGENT_NAME_FALLBACK = {
  feeding_state_assessment: "\u6444\u98DF\u72B6\u6001\u8BC4\u4F30",
  foodtable_inspector: "\u98DF\u53F0\u7167\u7247\u5206\u6790",
  disease_inspector: "\u75C5\u5BB3\u591A\u6A21\u6001\u8BCA\u65AD",
  usage_guide_agent: "\u4F7F\u7528\u6307\u5357"
};
var AGENT_NAME_REGISTRY = {};
function registerAgentNames(rows) {
  if (!Array.isArray(rows)) return;
  for (const r of rows) {
    if (!r) continue;
    const code = r.agentCode != null ? String(r.agentCode) : "";
    const name = r.agentName != null ? String(r.agentName).trim() : "";
    if (code && name) AGENT_NAME_REGISTRY[code] = name;
  }
}
function getToolDisplayName(toolName) {
  if (!toolName) return "\u5DE5\u5177";
  if (toolName.startsWith("agent_invoke_")) {
    const code = toolName.slice("agent_invoke_".length);
    const name = AGENT_NAME_REGISTRY[code] || AGENT_NAME_FALLBACK[code];
    return name ? "\u8C03\u7528\u667A\u80FD\u4F53\xB7" + name : "\u8C03\u7528\u5B50\u667A\u80FD\u4F53";
  }
  return TOOL_NAME_MAP[toolName] || "\u5DE5\u5177\u8C03\u7528";
}
var STAGE_NAME_MAP = {
  __pending_submit: "\u6B63\u5728\u63D0\u4EA4\u7ED9 AI \u670D\u52A1",
  InputValidation: "\u7406\u89E3\u95EE\u9898",
  AgentResolution: "\u8DEF\u7531\u667A\u80FD\u4F53",
  KnowledgeResolution: "\u68C0\u7D22\u77E5\u8BC6",
  ToolResolutionStage: "\u52A0\u8F7D\u5DE5\u5177",
  InputGuardrailStage: "\u8F93\u5165\u5B89\u5168\u68C0\u67E5",
  BalanceCheckStage: "\u989D\u5EA6\u68C0\u67E5",
  MemoryRetrievalStage: "\u53EC\u56DE\u8BB0\u5FC6",
  PromptAssemblyStage: "\u7EC4\u88C5\u4E0A\u4E0B\u6587",
  LlmExecutionStage: "\u6A21\u578B\u63A8\u7406",
  ConstrainedGenerationStage: "\u7EA6\u675F\u751F\u6210",
  OutputGuardrailStage: "\u8F93\u51FA\u5B89\u5168\u68C0\u67E5",
  BillingStage: "\u7528\u91CF\u8BA1\u8D39",
  MemoryFlushTriggerStage: "\u8BB0\u5FC6\u6574\u7406",
  PersistenceStage: "\u4FDD\u5B58\u4F1A\u8BDD"
};
function getStageDisplayName(name) {
  if (!name) return "\u5904\u7406\u4E2D";
  return STAGE_NAME_MAP[name] || "\u5176\u4ED6\u73AF\u8282";
}
var TURN_ACTIONS = [
  {
    key: "understand",
    title: "\u7406\u89E3\u95EE\u9898",
    desc: "\u6821\u9A8C\u8F93\u5165\u3001\u8BC6\u522B\u610F\u56FE\u3001\u8DEF\u7531\u5230\u5BF9\u5E94\u667A\u80FD\u4F53",
    stages: ["__pending_submit", "InputValidation", "InputGuardrailStage", "AgentResolution", "BalanceCheckStage"]
  },
  {
    key: "retrieve",
    title: "\u68C0\u7D22\u77E5\u8BC6",
    desc: "\u53EC\u56DE\u957F\u671F\u8BB0\u5FC6\u4E0E\u77E5\u8BC6\u5E93\u5185\u5BB9\uFF0C\u5E76\u7EC4\u88C5\u4E0A\u4E0B\u6587",
    stages: ["KnowledgeResolution", "MemoryRetrievalStage", "PromptAssemblyStage"]
  },
  {
    key: "tools",
    title: "\u8C03\u7528\u5DE5\u5177",
    desc: "\u6309\u9700\u8C03\u7528\u4E1A\u52A1\u5DE5\u5177\u83B7\u53D6\u771F\u5B9E\u6570\u636E",
    stages: ["ToolResolutionStage"]
  },
  {
    key: "generate",
    title: "\u751F\u6210\u56DE\u7B54",
    desc: "\u6A21\u578B\u63A8\u7406\u3001\u7EA6\u675F\u751F\u6210\u4E0E\u8F93\u51FA\u5B89\u5168\u68C0\u67E5",
    stages: ["LlmExecutionStage", "ConstrainedGenerationStage", "OutputGuardrailStage"]
  },
  {
    key: "finish",
    title: "\u6536\u5C3E",
    desc: "\u7528\u91CF\u8BA1\u8D39\u3001\u8BB0\u5FC6\u6574\u7406\u4E0E\u4F1A\u8BDD\u843D\u5E93",
    stages: ["BillingStage", "MemoryFlushTriggerStage", "PersistenceStage"]
  }
];
var STAGE_GROUP_MAP = (() => {
  const map = {};
  for (const action of TURN_ACTIONS) {
    for (const name of action.stages) map[name] = action.key;
  }
  return map;
})();
function worstStatus(list) {
  if (list.some((s) => s === "running")) return "running";
  if (list.some((s) => s === "error")) return "error";
  if (list.length > 0 && list.every((s) => s === "done")) return "done";
  return "pending";
}
function buildTurnActions(msg) {
  const stages = msg && msg.stages || [];
  const chain = msg && msg.toolChain || [];
  const buckets = {
    understand: [],
    retrieve: [],
    tools: [],
    generate: [],
    finish: []
  };
  for (const st of stages) {
    const key = STAGE_GROUP_MAP[st.name] || "generate";
    buckets[key].push({
      key: "s:" + st.name,
      label: getStageDisplayName(st.name),
      status: st.status,
      durationMs: st.durationMs || 0,
      detail: st.detail || "",
      kind: "stage"
    });
  }
  for (let i = 0; i < chain.length; i++) {
    const tc = chain[i];
    const running = !!tc.pending;
    buckets.tools.push({
      key: "t:" + i + ":" + (tc.toolName || ""),
      label: getToolDisplayName(tc.toolName),
      status: running ? "running" : tc.success === false ? "error" : "done",
      durationMs: tc.durationMs || 0,
      detail: summarizeToolOutcome(tc.result),
      kind: "tool",
      tool: tc
    });
  }
  return TURN_ACTIONS.map((def) => {
    const steps = buckets[def.key];
    const status = worstStatus(steps.map((s) => s.status));
    const durationMs = steps.reduce((a, s) => a + (s.durationMs || 0), 0);
    let summary = "";
    if (def.key === "tools") {
      const total = steps.length;
      if (total === 0) {
        summary = "";
      } else if (status === "running") {
        const cur = steps.find((s) => s.status === "running");
        summary = cur ? cur.label + "\u2026" : "\u51C6\u5907\u8C03\u7528";
      } else {
        const failed = steps.filter((s) => s.status === "error").length;
        summary = total + " \u6B21\u8C03\u7528" + (failed > 0 ? ` \xB7 ${failed} \u6B21\u5931\u8D25` : "");
      }
    } else if (status === "running") {
      const cur = steps.find((s) => s.status === "running");
      summary = cur ? cur.detail || cur.label : "\u8FDB\u884C\u4E2D";
    } else if (status === "error") {
      const bad = steps.find((s) => s.status === "error");
      summary = bad ? (bad.detail || bad.label) + " \u5931\u8D25" : "\u5931\u8D25";
    } else if (status === "done") {
      summary = steps.length > 1 ? `${steps.length} \u4E2A\u6B65\u9AA4` : steps[0]?.label || "";
    }
    return {
      key: def.key,
      title: def.title,
      desc: def.desc,
      status,
      durationMs,
      summary,
      steps,
      // 工具动作没有工具也没有工具阶段时不占位（普通问答不该出现空行）
      visible: def.key === "tools" ? steps.length > 0 : steps.length > 0
    };
  });
}
function turnCardSummary(msg) {
  const actions = buildTurnActions(msg).filter((a) => a.visible);
  if (!actions.length) return "";
  const running = actions.find((a) => a.status === "running");
  if (running) {
    const done = actions.filter((a) => a.status === "done").length;
    return "\u5904\u7406\u4E2D \xB7 " + running.title + ` \xB7 ${done}/${actions.length}`;
  }
  const total = actions.reduce((a, x) => a + (x.durationMs || 0), 0);
  const failed = actions.some((a) => a.status === "error");
  return (failed ? "\u5DF2\u5B8C\u6210\uFF08\u6709\u5931\u8D25\uFF09" : "\u5DF2\u5B8C\u6210") + ` \xB7 ${actions.length} \u4E2A\u52A8\u4F5C` + (total > 0 ? ` \xB7 \u5171 ${(total / 1e3).toFixed(1)}s` : "");
}
function isTurnCardExpanded(msg) {
  if (!msg) return false;
  if (msg._turnCardExpanded !== void 0) return msg._turnCardExpanded === true;
  return hasRunningStage(msg) || (msg.toolChain || []).some((t) => t.pending);
}
function hasRunningStage(msg) {
  return !!(msg && msg.stages && msg.stages.some((s) => s.status === "running"));
}
function stagePanelSummary(msg) {
  if (!msg || !msg.stages || !msg.stages.length) return "";
  const stages = msg.stages;
  const total = stages.length;
  const cur = stages.find((s) => s.status === "running");
  if (cur) {
    const done = stages.filter((s) => s.status !== "running").length;
    return "\u5904\u7406\u4E2D \xB7 " + getStageDisplayName(cur.name) + ` \xB7 ${done}/${total} \u6B65`;
  }
  const sum = stages.reduce((a, s) => a + (s.durationMs || 0), 0);
  return `\u5DF2\u5B8C\u6210 ${total} \u4E2A\u6B65\u9AA4` + (sum > 0 ? ` \xB7 \u5171 ${(sum / 1e3).toFixed(1)}s` : "");
}
function isStagePanelExpanded(msg) {
  if (!msg) return false;
  return msg._stagePanelExpanded === true;
}
function summarizeToolOutcome(resultStr) {
  if (!resultStr) return "";
  let r = null;
  try {
    r = typeof resultStr === "string" ? JSON.parse(resultStr) : resultStr;
  } catch {
    r = null;
  }
  if (!r || typeof r !== "object") return String(resultStr).slice(0, 48);
  if (r.summary) return String(r.summary).slice(0, 60);
  if (r.clarify === true) return "\u9700\u786E\u8BA4\uFF1A" + String(r.hint || r.message || "").slice(0, 36);
  if (r.success === false || typeof r.code === "number" && r.code >= 400 || r.error) {
    return "\u5931\u8D25\uFF1A" + String(r.msg || r.message || r.error || "").slice(0, 40);
  }
  const n = Array.isArray(r.data) ? r.data.length : typeof r.count === "number" ? r.count : null;
  if (n !== null) return n > 0 ? `\u8FD4\u56DE ${n} \u6761\u7ED3\u679C` : "\u65E0\u5339\u914D\u7ED3\u679C";
  if (r.success === true) return "\u6267\u884C\u6210\u529F";
  const keys = Object.keys(r);
  return keys.length ? "\u5DF2\u8FD4\u56DE " + keys.slice(0, 3).join("/") : "";
}
function flattenToolChainNodes(nodes, depth = 1) {
  if (depth > 3) return [];
  const out = [];
  for (const n of nodes) {
    out.push(n);
    if (Array.isArray(n.children) && depth < 3) {
      out.push(...flattenToolChainNodes(n.children, depth + 1));
    }
  }
  return out;
}
function toolFlowSummary(msg, includeChildren = false) {
  const raw = msg && msg.toolChain || [];
  const chain = includeChildren ? flattenToolChainNodes(raw) : raw;
  if (!chain.length) return "";
  const running = chain.some((t) => t.pending);
  const total = chain.reduce((a, t) => a + (t.durationMs || 0), 0);
  if (running) {
    const done = chain.filter((t) => !t.pending).length;
    return `${done}/${chain.length} \u6B65 \xB7 \u8FDB\u884C\u4E2D`;
  }
  return `${chain.length} \u6B65` + (total > 0 ? ` \xB7 ${(total / 1e3).toFixed(1)}s` : "");
}
function parseReasoningRounds(text) {
  if (!text) return [];
  const re = /【?思考轮\s*\d+\s*[｜|：:]\s*([^】\n]+)】?/g;
  const matches = [...text.matchAll(re)];
  if (!matches.length) return [];
  const rounds = [];
  for (let i = 0; i < matches.length; i++) {
    const m = matches[i];
    const numMatch = m[0].match(/思考轮\s*(\d+)/);
    const contentStart = m.index + m[0].length;
    const contentEnd = i + 1 < matches.length ? matches[i + 1].index : text.length;
    rounds.push({
      num: numMatch ? numMatch[1] : String(i + 1),
      title: String(m[1]).trim(),
      content: text.slice(contentStart, contentEnd).replace(/^\n+|\n+$/g, "")
    });
  }
  return rounds;
}
function getGroupTitle(group) {
  if (!group) return "";
  const rounds = parseReasoningRounds(group.reasoning);
  if (rounds.length > 0 && rounds[0].title) return rounds[0].title;
  if (group.tools && group.tools.length > 0) return getToolDisplayName(group.tools[0].toolName);
  return "";
}
function groupToolChainByRound(msg) {
  if (!msg || !msg.toolChain || msg.toolChain.length === 0) return [];
  const groups = [];
  const map = {};
  for (const tc of msg.toolChain) {
    const r = tc.round !== void 0 && tc.round !== null ? tc.round : 0;
    const key = String(r);
    if (!map[key]) {
      map[key] = { round: r, tools: [], reasoning: "", _expanded: false };
      groups.push(map[key]);
    }
    map[key].tools.push(tc);
  }
  for (const g of groups) {
    g.reasoning = g.tools[0] && g.tools[0].reasoning || "";
  }
  return groups;
}
function isToolChainGroupExpanded(msg, round) {
  if (!msg) return false;
  const state = msg._groupExpanded;
  if (state && state[String(round)] !== void 0) return state[String(round)] === true;
  const chain = msg.toolChain || [];
  return chain.some((t) => t.round === round && t.pending);
}
function openReportUrl(url) {
  if (!url) return;
  const g = typeof globalThis !== "undefined" ? globalThis : null;
  if (g && typeof g.open === "function") {
    g.open(url, "_blank");
    return;
  }
  if (g && g.window && typeof g.window.open === "function") {
    g.window.open(url, "_blank");
    return;
  }
}
function overallConfidenceValue(msg) {
  const traces = msg?.knowledgeTraces;
  if (!traces || traces.length === 0) return 0;
  return Math.max(...traces.map((t) => Number(t.confidence) || 0));
}
function getHitImages(msg) {
  const urls = [];
  const seen = /* @__PURE__ */ new Set();
  const traces = msg && msg.knowledgeTraces || [];
  for (const trace of traces) {
    const imgs = trace.images || [];
    for (const url of imgs) {
      if (url && !seen.has(url)) {
        seen.add(url);
        urls.push(url);
      }
    }
  }
  return urls;
}
function normalizeImageUrl(url) {
  if (!url) return "";
  return url;
}
var feedImageCache = /* @__PURE__ */ new WeakMap();
function getFeedRecordImages(msg) {
  const calls = msg && Array.isArray(msg.toolCalls) ? msg.toolCalls : [];
  const out = [];
  const seen = /* @__PURE__ */ new Set();
  for (const tc of calls) {
    if (!tc || tc.toolName !== "feed_record_query" || !tc.result) continue;
    let urls;
    const cached = feedImageCache.get(tc);
    if (cached && cached.raw === tc.result) {
      urls = cached.out;
    } else {
      urls = parseFeedRecordImageUrls(tc.result);
      feedImageCache.set(tc, { raw: tc.result, out: urls });
    }
    for (const url of urls) {
      if (!seen.has(url)) {
        seen.add(url);
        out.push(url);
      }
    }
  }
  return out;
}
function parseFeedRecordImageUrls(raw) {
  let result;
  try {
    result = JSON.parse(raw);
  } catch {
    return [];
  }
  const rows = result && Array.isArray(result.rows) ? result.rows : [];
  const out = [];
  for (const r of rows) {
    const url = normalizeImageUrl(r && r.imgUrl);
    if (url) out.push(url);
  }
  return out;
}
function overallConfidenceLevel(msg) {
  const conf = overallConfidenceValue(msg);
  if (conf >= 0.75) return "high";
  if (conf >= 0.5) return "medium";
  return "low";
}
function confidenceTagType(level) {
  if (level === "high") return "success";
  if (level === "medium") return "warning";
  return "danger";
}
function confidenceLabel(level) {
  if (level === "high") return "\u9AD8\u7F6E\u4FE1\u5EA6";
  if (level === "medium") return "\u4E2D\u7F6E\u4FE1\u5EA6";
  return "\u4F4E\u7F6E\u4FE1\u5EA6";
}
function matchSourceLabel(source) {
  if (source === "vector") return "\u5411\u91CF\u5339\u914D";
  if (source === "bm25") return "\u5173\u952E\u8BCD\u5339\u914D";
  if (source === "graph") return "\u56FE\u8C31\u5339\u914D";
  return "\u6DF7\u5408\u5339\u914D";
}

// core/src/ai/renderKey.ts
var renderKeySeq = 0;
function nextRenderKey() {
  renderKeySeq += 1;
  return renderKeySeq;
}
function bumpRenderKey(msg) {
  const token = nextRenderKey();
  if (msg) msg._renderKey = token;
  return token;
}

// core/src/ai/session.ts
var FLUSH_INTERVAL = 50;
var FLUSH_BUFFER_CHARS = 300;
var CHAT_IMG_STORAGE_PREFIX = "chatImages_";
var CHAT_IMG_CACHE_MAX = 12;
var RESUME_SEAM_MAX_OVERLAP = 120;
var RESUME_SEAM_MIN_OVERLAP = 8;
function createChatSession(options) {
  const notifier = options.notifier || noopNotifier;
  const storage = options.storage;
  const flushInterval = options.flushInterval ?? FLUSH_INTERVAL;
  const flushThreshold = options.flushThreshold ?? FLUSH_BUFFER_CHARS;
  let messages = [];
  let activeChatId = null;
  let loading = false;
  let msgIdCounter = 0;
  let onSaveSession = options.onSaveSession || null;
  let onAfterSend = options.onAfterSend || null;
  let controller = null;
  let contentBuffer = "";
  let thinkingBuffer = "";
  let contentTimer = null;
  let thinkingTimer = null;
  let streamTarget = null;
  const listeners = /* @__PURE__ */ new Set();
  function emit() {
    const snapshot = messages.slice();
    for (const listener of listeners) {
      try {
        listener(snapshot);
      } catch {
      }
    }
  }
  function nextMsgId() {
    msgIdCounter += 1;
    return "msg_" + Date.now() + "_" + msgIdCounter;
  }
  function currentStreamTarget() {
    if (streamTarget) return streamTarget;
    const last = messages[messages.length - 1];
    return last && last.role === "assistant" ? last : null;
  }
  function flushContent() {
    if (contentTimer) {
      clearTimeout(contentTimer);
      contentTimer = null;
    }
    if (!contentBuffer) return;
    const buffered = contentBuffer;
    contentBuffer = "";
    const target = currentStreamTarget();
    if (target) {
      target.content += buffered;
      target._renderKey = nextRenderKey();
      emit();
    }
  }
  function flushThinking() {
    if (thinkingTimer) {
      clearTimeout(thinkingTimer);
      thinkingTimer = null;
    }
    if (!thinkingBuffer) return;
    const buffered = thinkingBuffer;
    thinkingBuffer = "";
    const target = currentStreamTarget();
    if (target) {
      target.reasoning = (target.reasoning || "") + buffered;
      target._renderKey = nextRenderKey();
      emit();
    }
  }
  function appendContent(chunk) {
    contentBuffer += chunk;
    if (contentBuffer.length >= flushThreshold) {
      flushContent();
      return;
    }
    if (!contentTimer) {
      contentTimer = setTimeout(() => {
        contentTimer = null;
        flushContent();
      }, flushInterval);
    }
  }
  function appendThinking(chunk) {
    thinkingBuffer += chunk;
    if (thinkingBuffer.length >= flushThreshold) {
      flushThinking();
      return;
    }
    if (!thinkingTimer) {
      thinkingTimer = setTimeout(() => {
        thinkingTimer = null;
        flushThinking();
      }, flushInterval);
    }
  }
  function findAgentInvokeAt(chain) {
    for (let i = chain.length - 1; i >= 0; i--) {
      if (chain[i].pending && isAgentInvokeNode(chain[i])) return chain[i];
    }
    for (let i = chain.length - 1; i >= 0; i--) {
      if (isAgentInvokeNode(chain[i])) return chain[i];
    }
    return null;
  }
  function applyEvent(msg, event) {
    switch (event.type) {
      case "content":
        appendContent(event.content);
        break;
      case "thinking_content":
        appendThinking(event.content);
        break;
      case "progress": {
        msg.progress = { event: event.event, nodeName: event.nodeName, detail: event.detail };
        const stages = msg.stages || (msg.stages = []);
        if (event.event === "node_started") {
          for (const s of stages) if (s.status === "running") s.status = "done";
          stages.push({ name: event.nodeName, status: "running", detail: "", durationMs: null, startAt: Date.now() });
        } else if (event.event === "node_finished") {
          const st = stages.find((s) => s.name === event.nodeName && s.status === "running");
          if (st) {
            st.status = "done";
            st.durationMs = event.durationMs ?? null;
          }
        } else if (event.event === "node_progress") {
          const named = event.nodeName ? stages.find((s) => s.name === event.nodeName && s.status === "running") : null;
          if (named) {
            named.detail = event.detail || "";
          } else {
            for (let i = stages.length - 1; i >= 0; i--) {
              if (stages[i].status === "running") {
                stages[i].detail = event.detail || "";
                break;
              }
            }
          }
        } else if (event.event === "node_message") {
          const text = (event.detail || "").trim();
          if (text) {
            const notices = msg.notices || (msg.notices = []);
            if (!notices.includes(text)) notices.push(text);
          }
        } else if (event.event === "node_error") {
          const st = stages.find((s) => s.name === event.nodeName && s.status === "running");
          if (st) st.status = "error";
        }
        msg._renderKey = nextRenderKey();
        emit();
        break;
      }
      case "chatId": {
        if (event.chatId && !activeChatId) {
          activeChatId = event.chatId;
          if (storage) {
            try {
              const pending = storage.get(CHAT_IMG_STORAGE_PREFIX + "pending");
              if (pending) {
                const list = JSON.parse(pending);
                if (Array.isArray(list) && list.length > 0) {
                  const realKey = CHAT_IMG_STORAGE_PREFIX + event.chatId;
                  const realList = JSON.parse(storage.get(realKey) || "[]");
                  storage.set(realKey, JSON.stringify(realList.concat(list)));
                }
                storage.remove(CHAT_IMG_STORAGE_PREFIX + "pending");
              }
            } catch {
            }
          }
        }
        break;
      }
      case "tool_call_start": {
        const chain = msg.toolChain || (msg.toolChain = []);
        chain.push({
          toolName: event.toolName,
          arguments: event.arguments,
          result: "",
          success: null,
          durationMs: null,
          pending: true,
          startAt: Date.now(),
          reasoning: "",
          round: event.round,
          _idx: chain.length,
          _expanded: false
        });
        msg._renderKey = nextRenderKey();
        emit();
        break;
      }
      case "tool_call": {
        const record = {
          toolName: event.toolName,
          arguments: event.arguments,
          result: event.result,
          success: event.success,
          durationMs: event.durationMs ?? null,
          reasoning: event.reasoning || "",
          round: event.round
        };
        const calls = msg.toolCalls || (msg.toolCalls = []);
        calls.push(record);
        const chain = msg.toolChain || (msg.toolChain = []);
        let pending = null;
        const sameArgs = (a, b) => {
          if (a == null || b == null) return false;
          const norm = (v) => {
            try {
              return typeof v === "string" ? v : JSON.stringify(v);
            } catch {
              return String(v);
            }
          };
          return norm(a) === norm(b);
        };
        for (let i = 0; i < chain.length; i++) {
          const node = chain[i];
          if (node.pending && node.toolName === event.toolName && node.round === event.round && sameArgs(node.arguments, event.arguments)) {
            pending = node;
            break;
          }
        }
        if (!pending) {
          for (let i = 0; i < chain.length; i++) {
            const node = chain[i];
            if (node.pending && node.toolName === event.toolName && node.round === event.round) {
              pending = node;
              break;
            }
          }
        }
        if (pending) {
          pending.pending = false;
          pending.result = event.result;
          pending.success = event.success;
          pending.durationMs = event.durationMs ?? null;
          pending.reasoning = event.reasoning || "";
        } else {
          chain.push({ ...record, pending: false, _idx: chain.length, _expanded: false });
        }
        if (isWebSearchTool(event.toolName)) {
          const { results, optimizedQuery } = parseWebSearchResults([
            { toolName: event.toolName, result: event.result }
          ]);
          if (results.length > 0) {
            msg.webSearchResults = [...msg.webSearchResults || [], ...results];
            msg.optimizedSearchQuery = optimizedQuery || msg.optimizedSearchQuery || "";
          }
        }
        msg._renderKey = nextRenderKey();
        emit();
        break;
      }
      // 2026-11-04 父子过程展开：子智能体过程快照挂到对应的 agent_invoke 节点
      case "child_trace": {
        const chain = msg.toolChain || (msg.toolChain = []);
        const depth = Number(event.depth) || 1;
        const rawTools = Array.isArray(event.tool_chain) ? event.tool_chain : [];
        const rawStages = Array.isArray(event.stages) ? event.stages : [];
        const childNodes = rawTools.map((raw, i) => {
          const r = raw && typeof raw === "object" ? raw : {};
          return {
            toolName: String(r.tool_name ?? r.toolName ?? ""),
            arguments: r.arguments,
            result: r.result,
            success: typeof r.success === "boolean" ? r.success : null,
            durationMs: typeof r.duration_ms === "number" ? r.duration_ms : typeof r.durationMs === "number" ? r.durationMs : null,
            round: typeof r.round === "number" ? r.round : void 0,
            pending: false,
            _idx: i,
            _expanded: false,
            depth
          };
        });
        const childStages = rawStages.map((s) => ({
          name: String(s.node_name ?? ""),
          status: "done",
          detail: s.detail != null ? String(s.detail) : "",
          durationMs: typeof s.duration_ms === "number" ? s.duration_ms : null
        }));
        let level = chain;
        const maxDepth = Math.min(depth, 3);
        for (let d = 1; d < maxDepth; d++) {
          const pivot = findAgentInvokeAt(level);
          if (!pivot || !Array.isArray(pivot.children) || pivot.children.length === 0) {
            level = [];
            break;
          }
          level = pivot.children;
        }
        const parent = findAgentInvokeAt(level);
        if (parent) {
          parent.children = childNodes;
          parent.childStages = childStages;
          parent.agentCode = event.agent_code;
          parent._childrenExpanded = false;
        }
        msg._renderKey = nextRenderKey();
        emit();
        break;
      }
      case "sources":
        msg.sources = event.sources;
        msg._renderKey = nextRenderKey();
        emit();
        break;
      case "knowledgeTraces":
        msg.knowledgeTraces = event.knowledgeTraces;
        msg._renderKey = nextRenderKey();
        emit();
        break;
      case "clarification": {
        flushContent();
        const options2 = Array.isArray(event.options) ? event.options : [];
        if (options2.length === 0) {
          const text = (event.message || "").trim();
          if (text) {
            const notices = msg.notices || (msg.notices = []);
            if (!notices.includes(text)) notices.push(text);
          }
          msg._renderKey = nextRenderKey();
          emit();
          break;
        }
        msg.content = "";
        msg.clarification = {
          message: event.message || "",
          options: options2
        };
        msg._renderKey = nextRenderKey();
        emit();
        break;
      }
      case "usage":
        flushContent();
        msg.usage = event.usage;
        emit();
        break;
      case "finish":
        if (event.reason && event.reason !== "stop") {
          msg.truncatedByLength = event.reason === "length";
          if (!msg.truncatedByLength) {
            msg.finishReason = event.reason;
          }
          msg._renderKey = nextRenderKey();
          emit();
        }
        break;
      case "error":
        flushContent();
        appendContent("\n\n> **\u9519\u8BEF:** " + (event.content || "\u672A\u77E5\u9519\u8BEF"));
        flushContent();
        break;
      case "done":
        break;
    }
  }
  async function runStream(target, payload) {
    contentBuffer = "";
    thinkingBuffer = "";
    streamTarget = target;
    loading = true;
    emit();
    onAfterSend && onAfterSend();
    controller = createAbortController();
    try {
      const streamPayload = payload.chatId === void 0 && activeChatId != null ? { ...payload, chatId: activeChatId } : payload;
      for await (const event of options.transport.streamChat(streamPayload, controller.signal)) {
        if (event.type === "done") {
          flushContent();
          flushThinking();
          break;
        }
        applyEvent(target, event);
      }
    } catch (err) {
      if (err && (err.name === "AbortError" || err.code === 20)) {
        target.cancelled = true;
      } else {
        flushContent();
        flushThinking();
        if (target.content) {
          target.cancelled = true;
          notifier.error && notifier.error("\u751F\u6210\u4E2D\u65AD\uFF0C\u5DF2\u4FDD\u7559\u5DF2\u751F\u6210\u5185\u5BB9");
        } else {
          notifier.error && notifier.error("\u8BF7\u6C42\u5931\u8D25");
          target.content = err && err.message || "\u8BF7\u6C42\u5931\u8D25\uFF0C\u8BF7\u91CD\u8BD5\u3002";
        }
        emit();
      }
    } finally {
      flushContent();
      flushThinking();
      if (target.stages) {
        for (const s of target.stages) if (s.status === "running") s.status = "done";
      }
      target.streaming = false;
      loading = false;
      controller = null;
      streamTarget = null;
      emit();
    }
  }
  async function send(payload) {
    if (loading) return;
    const question = (payload.question || "").trim();
    const hasImage = !!payload.imageDataUrl;
    if (!question && !hasImage) return;
    const userContent = question || (hasImage ? "[Image]" : "");
    if (hasImage && payload.imageDataUrl) {
      saveChatImage(activeChatId || "pending", payload.imageDataUrl);
    }
    const userMsg = {
      _id: nextMsgId(),
      role: "user",
      content: userContent,
      createTime: currentTime(),
      image: payload.imageDataUrl || void 0
    };
    const assistantMsg = {
      _id: nextMsgId(),
      role: "assistant",
      content: "",
      createTime: currentTime(),
      usage: null,
      cancelled: false,
      progress: null,
      toolCalls: null,
      webSearchResults: null,
      optimizedSearchQuery: null,
      knowledgeTraces: null,
      reasoning: "",
      _reasoningExpanded: true,
      toolChain: [],
      _loopExpanded: true,
      stages: [
        {
          name: "__pending_submit",
          status: "running",
          detail: "\u6B63\u5728\u63D0\u4EA4\u7ED9 AI \u670D\u52A1\u2026",
          durationMs: null,
          startAt: Date.now()
        }
      ],
      _renderKey: 0,
      streaming: true
    };
    messages = [...messages, userMsg, assistantMsg];
    await runStream(assistantMsg, payload);
    const answer = assistantMsg.content;
    if (answer && !assistantMsg.cancelled && onSaveSession) {
      try {
        onSaveSession(userContent, answer);
      } catch {
      }
    }
  }
  async function resume(msg, base) {
    if (loading) return;
    const prefix = msg.content || "";
    if (!prefix.trim()) return;
    const originQuestion = findQuestionBefore(msg);
    msg.cancelled = false;
    msg.interrupted = false;
    msg.truncatedByLength = false;
    msg.streaming = true;
    msg.progress = null;
    msg.stages = [];
    msg.toolChain = [];
    msg.toolCalls = null;
    msg._turnCardExpanded = void 0;
    msg._renderKey = nextRenderKey();
    await runStream(msg, {
      ...base || {},
      question: "\u7EE7\u7EED",
      resumePrefix: prefix,
      resumeQuestion: originQuestion || void 0
    });
    stitchResumeSeam(msg, prefix);
  }
  function stitchResumeSeam(msg, prefix) {
    const full = msg.content || "";
    if (!prefix || !full.startsWith(prefix) || full.length <= prefix.length) return;
    const continuation = full.slice(prefix.length);
    const maxOverlap = Math.min(RESUME_SEAM_MAX_OVERLAP, prefix.length, continuation.length);
    for (let size = maxOverlap; size >= RESUME_SEAM_MIN_OVERLAP; size--) {
      if (prefix.slice(prefix.length - size) === continuation.slice(0, size)) {
        msg.content = prefix + continuation.slice(size);
        msg._renderKey = nextRenderKey();
        emit();
        return;
      }
    }
  }
  function findQuestionBefore(msg) {
    const idx = messages.findIndex((m) => m._id === msg._id);
    for (let i = (idx >= 0 ? idx : messages.length) - 1; i >= 0; i--) {
      if (messages[i].role === "user") return messages[i].content || "";
    }
    return "";
  }
  function saveChatImage(chatId, dataUrl) {
    if (!storage || !chatId || !dataUrl) return;
    try {
      const key = CHAT_IMG_STORAGE_PREFIX + chatId;
      const list = JSON.parse(storage.get(key) || "[]");
      list.push(dataUrl);
      if (list.length > CHAT_IMG_CACHE_MAX) {
        list.splice(0, list.length - CHAT_IMG_CACHE_MAX);
      }
      storage.set(key, JSON.stringify(list));
    } catch {
    }
  }
  function loadChatImages(chatId) {
    if (!storage || !chatId) return [];
    try {
      const list = JSON.parse(storage.get(CHAT_IMG_STORAGE_PREFIX + chatId) || "[]");
      return Array.isArray(list) ? list : [];
    } catch {
      return [];
    }
  }
  function clearChatImages(chatId) {
    if (!storage || !chatId) return;
    try {
      storage.remove(CHAT_IMG_STORAGE_PREFIX + chatId);
    } catch {
    }
  }
  function sendClarification(msg, option, base) {
    const draft = String(msg.clarificationDraft || "").trim();
    const refined = option && option.value || draft;
    if (!refined) return;
    msg.clarificationDraft = "";
    const payload = { ...base || {}, question: refined };
    return send(payload);
  }
  return {
    getMessages: () => messages.slice(),
    getActiveChatId: () => activeChatId,
    setActiveChatId: (id) => {
      activeChatId = id;
    },
    isLoading: () => loading,
    send,
    resume,
    cancel() {
      if (controller) controller.abort();
      loading = false;
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    flushNow() {
      flushContent();
      flushThinking();
    },
    reset() {
      messages = [];
      contentBuffer = "";
      thinkingBuffer = "";
      activeChatId = null;
      emit();
    },
    dispose() {
      if (contentTimer) clearTimeout(contentTimer);
      if (thinkingTimer) clearTimeout(thinkingTimer);
      contentTimer = null;
      thinkingTimer = null;
      if (controller) controller.abort();
      listeners.clear();
    },
    setOnSaveSession(cb) {
      onSaveSession = cb;
    },
    setOnAfterSend(cb) {
      onAfterSend = cb;
    },
    saveChatImage,
    loadChatImages,
    clearChatImages,
    sendClarification
  };
}

// core/src/ai/protocol.ts
function createChatProtocolParser() {
  let buffer = "";
  let finished = false;
  const handleLine = (rawLine, out) => {
    const trimmed = rawLine.trim();
    if (!trimmed) return;
    let jsonStr = trimmed;
    if (jsonStr.startsWith("data:")) {
      jsonStr = jsonStr.slice(jsonStr.indexOf(" ") === -1 ? 5 : jsonStr.indexOf(" ") + 1).trim();
    }
    if (jsonStr === "[DONE]") {
      finished = true;
      out.push({ type: "done" });
      return;
    }
    let parsed;
    try {
      parsed = JSON.parse(jsonStr);
    } catch {
      return;
    }
    if (!parsed || typeof parsed !== "object") return;
    if (parsed.event && parsed.nodeName) {
      out.push({
        type: "progress",
        event: String(parsed.event),
        nodeName: String(parsed.nodeName),
        detail: parsed.detail || "",
        durationMs: parsed.duration_ms
      });
      return;
    }
    if (parsed.type === "clarification") {
      out.push({
        type: "clarification",
        message: parsed.message || "",
        options: Array.isArray(parsed.options) ? parsed.options : []
      });
      return;
    }
    if (parsed.type === "tool_call_start") {
      out.push({
        type: "tool_call_start",
        toolName: String(parsed.tool_name || parsed.toolName || ""),
        arguments: parsed.arguments,
        round: parsed.round
      });
      return;
    }
    if (parsed.type === "tool_call") {
      out.push({
        type: "tool_call",
        toolName: String(parsed.tool_name || parsed.toolName || ""),
        arguments: parsed.arguments,
        result: parsed.result,
        success: parsed.success,
        durationMs: parsed.duration_ms ?? parsed.durationMs ?? null,
        reasoning: parsed.reasoning || "",
        round: parsed.round
      });
      return;
    }
    if (parsed.type === "child_trace") {
      const f = parsed;
      const evt = {
        type: "child_trace",
        depth: Number(f.depth) || 1,
        agent_code: typeof f.agent_code === "string" ? f.agent_code : void 0,
        stages: Array.isArray(f.stages) ? f.stages : [],
        tool_chain: Array.isArray(f.tool_chain) ? f.tool_chain : []
      };
      out.push(evt);
      return;
    }
    const choice = parsed.choices && parsed.choices[0];
    const delta = choice && choice.delta;
    const content = delta && delta.content || parsed.content || parsed.message && parsed.message.content || "";
    if (content) out.push({ type: "content", content });
    const thinkingContent = delta && delta.thinking_content || parsed.thinking_content || "";
    if (thinkingContent) out.push({ type: "thinking_content", content: thinkingContent });
    if (parsed.chatId) out.push({ type: "chatId", chatId: String(parsed.chatId) });
    if (parsed.chat_id) out.push({ type: "chatId", chatId: String(parsed.chat_id) });
    if (Array.isArray(parsed.knowledgeTraces) && parsed.knowledgeTraces.length > 0) {
      out.push({ type: "knowledgeTraces", knowledgeTraces: parsed.knowledgeTraces });
    }
    if (Array.isArray(parsed.sources) && parsed.sources.length > 0) {
      out.push({ type: "sources", sources: parsed.sources });
    }
    if (parsed.usage) {
      out.push({ type: "usage", usage: parsed.usage });
    }
    const finishReason = choice && choice.finish_reason ? String(choice.finish_reason) : "";
    if (finishReason && finishReason !== "stop") {
      out.push({ type: "finish", reason: finishReason });
    }
    if (parsed.done || finishReason === "stop") {
      finished = true;
      out.push({ type: "done" });
    }
  };
  return {
    push(chunk) {
      const out = [];
      buffer += chunk;
      const lines = buffer.split("\n");
      buffer = lines.pop() ?? "";
      for (const line of lines) {
        if (finished) break;
        handleLine(line, out);
      }
      return out;
    },
    flush() {
      const out = [];
      if (buffer && !finished) {
        handleLine(buffer, out);
        buffer = "";
      }
      if (!finished) {
        finished = true;
        out.push({ type: "done" });
      }
      return out;
    }
  };
}

// core/src/ai/transport.ts
function buildChatRequestBody(payload) {
  const hasImage = !!payload.imageDataUrl;
  const messageContent = hasImage ? [
    ...payload.question ? [{ type: "text", text: payload.question }] : [],
    { type: "image_url", image_url: { url: payload.imageDataUrl } }
  ] : payload.question || "";
  const body = {
    model: payload.model || payload.agentCode || "default",
    messages: [{ role: "user", content: messageContent }],
    stream: payload.stream === void 0 ? true : payload.stream,
    temperature: payload.temperature,
    max_tokens: payload.maxTokens,
    top_p: payload.topP,
    frequency_penalty: payload.frequencyPenalty,
    presence_penalty: payload.presencePenalty,
    tools: payload.tools,
    tool_choice: payload.toolChoice,
    chat_id: payload.chatId,
    agent_id: payload.agentId,
    knowledge_ids: payload.knowledgeIds,
    rag_type: payload.ragType,
    // 结构化澄清选择：后端直接按 placeId 等权威字段继续，不再靠名称重查
    clarify_choice: payload.clarifyChoice,
    // 续写（中断后「继续生成」）：前缀是已显示正文，原问题供后端检索使用
    resume_prefix: payload.resumePrefix,
    resume_question: payload.resumeQuestion,
    // 恒发 boolean：`|| undefined` 会把 false 吃掉，导致后端关不掉深度思考
    thinking: payload.thinking
  };
  for (const key of Object.keys(body)) {
    if (body[key] === void 0) delete body[key];
  }
  return body;
}
function toNativeAbortSignal(signal) {
  const g = globalThis;
  if (!signal) return void 0;
  if (typeof g.AbortSignal === "function" && signal instanceof g.AbortSignal) return signal;
  const Native = g.AbortController;
  if (typeof Native !== "function") return signal;
  let ctrl;
  try {
    ctrl = new Native();
  } catch {
    return signal;
  }
  if (!ctrl || !ctrl.signal) return signal;
  if (signal.aborted) {
    ctrl.abort();
    return ctrl.signal;
  }
  if (typeof signal.addEventListener === "function") {
    ;
    signal.addEventListener(
      "abort",
      () => {
        try {
          ctrl.abort();
        } catch {
        }
      },
      { once: true }
    );
  }
  return ctrl.signal;
}
function createFetchTransport(options) {
  const endpoint = options.endpoint || "/v1/chat/completions";
  const url = options.baseUrl.replace(/\/$/, "") + endpoint;
  return {
    async *streamChat(payload, signal) {
      const fetchImpl = options.fetchImpl || globalThis.fetch;
      if (typeof fetchImpl !== "function") {
        yield { type: "error", content: "\u5F53\u524D\u73AF\u5883\u4E0D\u652F\u6301 fetch\uFF0C\u8BF7\u6CE8\u5165\u81EA\u5B9A\u4E49 transport" };
        yield { type: "done" };
        return;
      }
      const headers = {
        "Content-Type": "application/json",
        ...options.headers || {}
      };
      const token = options.getToken ? options.getToken() : null;
      if (token) headers.Authorization = "Bearer " + token;
      let res;
      try {
        res = await fetchImpl(url, {
          method: "POST",
          headers,
          body: JSON.stringify(buildChatRequestBody(payload)),
          // 不能直接把内核的 AbortSignalLike 交给浏览器 fetch（brand check 会拒绝）
          signal: toNativeAbortSignal(signal)
        });
      } catch (err) {
        if (err && (err.name === "AbortError" || err.code === 20)) {
          yield { type: "done" };
          return;
        }
        yield { type: "error", content: err && err.message || "\u7F51\u7EDC\u8BF7\u6C42\u5931\u8D25" };
        yield { type: "done" };
        return;
      }
      if (!res.ok) {
        let message = "\u8BF7\u6C42\u5931\u8D25\uFF1AHTTP " + res.status;
        let errorType = "server_error";
        let errorCode = "internal_error";
        try {
          const text = await res.text();
          if (text) {
            try {
              const parsed = JSON.parse(text);
              if (parsed && parsed.error) {
                message = parsed.error.message || message;
                errorType = parsed.error.type || errorType;
                errorCode = parsed.error.code || errorCode;
              } else {
                message = text;
              }
            } catch {
              message = text;
            }
          }
        } catch {
        }
        yield { type: "error", content: message, errorType, errorCode, statusCode: res.status };
        yield { type: "done" };
        return;
      }
      const parser = createChatProtocolParser();
      if (!res.body || typeof res.body.getReader !== "function") {
        const text = await res.text();
        for (const ev of parser.push(text)) yield ev;
        for (const ev of parser.flush()) yield ev;
        return;
      }
      const TD = globalThis.TextDecoder;
      const decoder = TD ? new TD("utf-8") : null;
      const reader = res.body.getReader();
      try {
        for (; ; ) {
          const { done, value } = await reader.read();
          if (done) break;
          const text = decoder ? decoder.decode(value, { stream: true }) : String(value);
          for (const ev of parser.push(text)) {
            yield ev;
            if (ev.type === "done") return;
          }
        }
      } finally {
        const flushed = parser.flush();
        for (const ev of flushed) {
          if (ev.type === "done") return;
          yield ev;
        }
      }
    }
  };
}

// core/src/vue/runtime.ts
var defaultStorage = createMemoryStorage();
var runtime = {
  storage: defaultStorage,
  notify: {},
  api: {},
  renderMarkdown: (md) => md || ""
};
function configureAiRuntime(patch) {
  runtime = {
    ...runtime,
    ...patch,
    storage: patch.storage || runtime.storage,
    notify: { ...runtime.notify, ...patch.notify || {} },
    api: { ...runtime.api, ...patch.api || {} }
  };
}
function getAiRuntime() {
  return runtime;
}
function resetAiRuntime() {
  runtime = {
    storage: defaultStorage,
    notify: {},
    api: {},
    renderMarkdown: (md) => md || ""
  };
}
function rtGet(key) {
  try {
    return runtime.storage.get(key);
  } catch {
    return null;
  }
}
function rtSet(key, value) {
  try {
    runtime.storage.set(key, value);
  } catch {
  }
}
function rtRemove(key) {
  try {
    runtime.storage.remove(key);
  } catch {
  }
}

// core/src/vue/useChatStream.ts
var CHAT_IMG_MAX_WIDTH = 800;
var CHAT_IMG_JPEG_QUALITY = 0.7;
var CHAT_IMG_SEND_MAX_WIDTH = 1600;
var CHAT_IMG_SEND_QUALITY = 0.85;
var CHAT_IMG_SKIP_COMPRESS_BYTES = 300 * 1024;
function browserStorage() {
  const ls = globalThis.localStorage;
  if (!ls) return createMemoryStorage();
  return {
    get: (k) => {
      try {
        return ls.getItem(k);
      } catch {
        return null;
      }
    },
    set: (k, v) => {
      try {
        ls.setItem(k, v);
      } catch {
      }
    },
    remove: (k) => {
      try {
        ls.removeItem(k);
      } catch {
      }
    }
  };
}
function useChatStream(options = {}) {
  const rt = getAiRuntime();
  const storage = options.storage || rt.storage || browserStorage();
  const notifier = options.notifier || {
    // 优先用运行时注册的提示层（各端 ElMessage / uni.showToast），未注册时退回 console
    warn: (m) => rt.notify.warning ? rt.notify.warning(m) : console.warn("[chat]", m),
    error: (m) => rt.notify.error ? rt.notify.error(m) : console.error("[chat]", m),
    info: (m) => rt.notify.info ? rt.notify.info(m) : console.info("[chat]", m)
  };
  const transport = options.transport || createFetchTransport({
    baseUrl: options.transportOptions?.baseUrl || "",
    getToken: options.transportOptions?.getToken,
    headers: options.transportOptions?.headers
  });
  const session = createChatSession({
    transport,
    storage,
    notifier,
    onSaveSession: options.onSaveSession
  });
  const messages = shallowRef([]);
  const loading = ref(false);
  const inputText = ref("");
  const messagesRef = ref(null);
  const chatIdTick = ref(0);
  const activeChatId = computed({
    get: () => {
      void chatIdTick.value;
      return session.getActiveChatId();
    },
    set: (v) => {
      session.setActiveChatId(v);
      chatIdTick.value++;
    }
  });
  session.subscribe((list) => {
    messages.value = list;
    loading.value = session.isLoading();
    chatIdTick.value++;
    followScroll();
  });
  let msgIdCounter = 0;
  const nextMsgId = () => "msg_" + Date.now() + "_" + ++msgIdCounter;
  const imageFile = ref(null);
  const imagePreview = ref("");
  function handleImageSelect(file) {
    if (!file) return;
    const fileType = file.type || "";
    const ext = (String(file.name || "").split(".").pop() || "").toLowerCase();
    const isImage = fileType.startsWith("image/") || ["jpg", "jpeg", "png", "webp", "gif", "bmp"].includes(ext);
    if (!isImage) {
      notifier.warn && notifier.warn("\u8BF7\u9009\u62E9\u56FE\u7247\u6587\u4EF6");
      return;
    }
    const supported2 = ["image/jpeg", "image/png", "image/webp", "image/gif", "image/bmp"];
    if (fileType && !supported2.includes(fileType)) {
      notifier.warn && notifier.warn("\u4E0D\u652F\u6301\u7684\u56FE\u7247\u683C\u5F0F\uFF0C\u8BF7\u4E0A\u4F20 JPG/PNG/WEBP/GIF/BMP");
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      notifier.warn && notifier.warn("\u56FE\u7247\u4E0D\u80FD\u8D85\u8FC7 20MB");
      return;
    }
    imageFile.value = file;
    const FR = globalThis.FileReader;
    if (!FR) return;
    const reader = new FR();
    reader.onload = (ev) => {
      const raw = ev.target.result;
      imagePreview.value = raw;
      if (fileType === "image/gif" || ext === "gif") {
        return;
      }
      if (file.size && file.size <= CHAT_IMG_SKIP_COMPRESS_BYTES) {
        return;
      }
      void compressForSend(raw).then((compressed) => {
        if (imageFile.value !== file) return;
        imagePreview.value = compressed;
      });
    };
    reader.readAsDataURL(file);
  }
  function removeImage() {
    imageFile.value = null;
    imagePreview.value = "";
  }
  const inputPlaceholder = computed(() => imagePreview.value ? "\u8BF4\u70B9\u4EC0\u4E48\u5427..." : "\u53D1\u9001\u6D88\u606F...");
  const canSend = computed(() => !!((inputText.value.trim() || imagePreview.value) && options.effectiveAgent?.value));
  let scrollTimer = null;
  const AUTO_FOLLOW_THRESHOLD = 80;
  const scrollDetached = ref(false);
  function isNearBottom(el) {
    if (!el) return true;
    const { scrollHeight, scrollTop, clientHeight } = el;
    if (typeof scrollHeight !== "number") return true;
    return scrollHeight - scrollTop - clientHeight <= AUTO_FOLLOW_THRESHOLD;
  }
  function handleScroll() {
    const el = messagesRef.value;
    if (!el || typeof el.scrollTop !== "number") return;
    scrollDetached.value = !isNearBottom(el);
  }
  function scrollToBottom() {
    if (scrollTimer) return;
    scrollTimer = setTimeout(() => {
      scrollTimer = null;
      nextTick(() => {
        const el = messagesRef.value;
        if (el) el.scrollTop = el.scrollHeight;
      });
    }, 100);
  }
  function scrollToBottomImmediate() {
    if (scrollTimer) {
      clearTimeout(scrollTimer);
      scrollTimer = null;
    }
    nextTick(() => {
      const el = messagesRef.value;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }
  function followScroll() {
    if (scrollDetached.value) return;
    scrollToBottom();
  }
  function scrollToLatest() {
    scrollDetached.value = false;
    scrollToBottomImmediate();
  }
  watch(messagesRef, (el, old) => {
    if (old && typeof old.removeEventListener === "function") {
      old.removeEventListener("scroll", handleScroll);
    }
    if (el && typeof el.addEventListener === "function") {
      el.addEventListener("scroll", handleScroll, { passive: true });
    }
  });
  function buildBasePayload(agentCode) {
    return {
      agentCode,
      agentId: options.effectiveAgentObj?.value?.id,
      knowledgeIds: (options.contextKbs?.value?.length || 0) > 0 ? options.contextKbs.value.map((v) => Number(v)) : (options.effectiveKnowledges?.value || []).map((k) => Number(k.id)),
      // 只透传页面显式给出的 RAG 策略：各端对 ragType 的语义不同
      // （后台由后端按智能体配置自行决定，AI 前台由用户下拉显式选择）
      ragType: options.ragType?.value || void 0,
      chatId: activeChatId.value || void 0,
      thinking: !!options.thinkingEnabled?.value
    };
  }
  async function handleSend(opts) {
    const question = inputText.value.trim();
    const image = imagePreview.value;
    if (!question && !image || loading.value) return;
    const agentCode = options.effectiveAgent?.value;
    if (!agentCode) return;
    const clarifyChoice = opts && typeof opts === "object" && !opts.target && "clarifyChoice" in opts ? opts.clarifyChoice : void 0;
    const capturedPreview = image;
    removeImage();
    inputText.value = "";
    const payload = {
      ...buildBasePayload(agentCode),
      question,
      // 此时 capturedPreview 通常已是发送档（长边 ≤1600 / JPEG 0.85）；
      // 选图后极快点击发送才会拿到原图，属可接受的降级（只是没享受到压缩）
      imageDataUrl: capturedPreview || void 0,
      clarifyChoice
    };
    if (capturedPreview) {
      compressImage(capturedPreview).then((compressed) => {
        session.saveChatImage(activeChatId.value || "pending", compressed);
      });
    }
    scrollToLatest();
    await session.send(payload);
    scrollToBottomImmediate();
  }
  async function handleResume(msg) {
    if (loading.value || !msg) return;
    const agentCode = options.effectiveAgent?.value;
    if (!agentCode) return;
    scrollToLatest();
    await session.resume(msg, buildBasePayload(agentCode));
    scrollToBottomImmediate();
  }
  function compressBy(dataUrl, maxWidth, quality) {
    const ImageCtor = globalThis.Image;
    const documentCtor = globalThis.document;
    if (!ImageCtor || !documentCtor || !dataUrl || !dataUrl.startsWith("data:image")) {
      return Promise.resolve(dataUrl);
    }
    return new Promise((resolve2) => {
      const img = new ImageCtor();
      img.onload = () => {
        try {
          const ratio = img.width > maxWidth ? maxWidth / img.width : 1;
          const w = Math.round(img.width * ratio);
          const h = Math.round(img.height * ratio);
          const canvas = documentCtor.createElement("canvas");
          canvas.width = w;
          canvas.height = h;
          canvas.getContext("2d").drawImage(img, 0, 0, w, h);
          const out = canvas.toDataURL("image/jpeg", quality);
          resolve2(out && out.length < dataUrl.length ? out : dataUrl);
        } catch {
          resolve2(dataUrl);
        }
      };
      img.onerror = () => resolve2(dataUrl);
      img.src = dataUrl;
    });
  }
  function compressForSend(dataUrl) {
    return compressBy(dataUrl, CHAT_IMG_SEND_MAX_WIDTH, CHAT_IMG_SEND_QUALITY);
  }
  function compressImage(dataUrl) {
    return compressBy(dataUrl, CHAT_IMG_MAX_WIDTH, CHAT_IMG_JPEG_QUALITY);
  }
  function handleCancel() {
    session.cancel();
    loading.value = false;
  }
  function handleClarificationSend(msg, option) {
    if (loading.value) return;
    const draft = String(msg.clarificationDraft || "").trim();
    const refined = option && option.value || draft;
    if (!refined) return;
    msg.clarificationDraft = "";
    inputText.value = refined;
    void handleSend();
  }
  return {
    // 基础状态
    messages,
    loading,
    inputText,
    activeChatId,
    messagesRef,
    nextMsgId,
    // 图片
    imageFile,
    imagePreview,
    handleImageSelect,
    removeImage,
    loadChatImagesFromStorage: (chatId) => session.loadChatImages(chatId),
    clearChatImagesFromStorage: (chatId) => session.clearChatImages(chatId),
    // computed
    inputPlaceholder,
    canSend,
    // 滚动 / 缓冲
    scrollToBottom,
    scrollToBottomImmediate,
    scrollToLatest,
    scrollDetached,
    followScroll,
    flushBuffer: () => session.flushNow(),
    appendToBuffer: () => {
    },
    // 发送 / 取消 / 澄清
    handleSend,
    handleResume,
    handleCancel,
    cancelStream: handleCancel,
    handleClarificationSend,
    // 回调挂载
    setOnSaveSession: (cb) => session.setOnSaveSession(cb),
    setOnAfterSend: (cb) => session.setOnAfterSend(cb),
    // 直接拿内核（AI 前端等需要 reset / 自定义聊天流时用）
    session,
    dispose: () => session.dispose()
  };
}

// core/src/vue/useChatAgent.ts
import { ref as ref2, computed as computed2, watch as watch2 } from "vue";
var CHAT_CACHE_KEYS = {
  agent: "ragChat_selectedAgent",
  ragType: "ragChat_selectedRagType",
  viewDept: "ragChat_viewDept",
  thinking: "ragChat_thinking"
};
function isSystemAgent(agent) {
  return !!agent && (agent.agentType === "system" || String(agent.agentCode || "").startsWith("sys_"));
}
function useChatAgent(options = {}) {
  let onAgentChanged = options.onAgentChanged || (() => {
  });
  function setOnAgentChanged(cb) {
    onAgentChanged = cb || (() => {
    });
  }
  const agentList = ref2([]);
  const selectedAgent = ref2("");
  const selectedRagType = ref2("");
  const agentLoading = ref2(false);
  const kbLoading = ref2(false);
  const ragLoading = ref2(false);
  const allKnowledges = ref2([]);
  const allRagStrategies = ref2([]);
  const thinkingEnabled = ref2(rtGet(CHAT_CACHE_KEYS.thinking) === "true");
  function handleThinkingChange() {
    rtSet(CHAT_CACHE_KEYS.thinking, String(thinkingEnabled.value));
  }
  const selectedAgentObj = computed2(
    () => agentList.value.find((a) => a.agentCode === selectedAgent.value) || null
  );
  const agentSupportsImage = computed2(() => {
    const agent = effectiveAgentObj.value;
    if (!agent || !agent.modelCapabilities) return false;
    return String(agent.modelCapabilities).split(",").map((s) => s.trim()).includes("vision");
  });
  watch2(
    () => selectedAgentObj.value && selectedAgentObj.value.id,
    (agentId) => {
      if (agentId) onAgentChanged(agentId);
    },
    { immediate: true }
  );
  const filteredRagStrategies = computed2(() => {
    const agent = selectedAgentObj.value;
    const types = (agent && agent.ragType ? agent.ragType : "").split(",").map((s) => s.trim()).filter(Boolean);
    const set = new Set(types);
    return allRagStrategies.value.filter((s) => set.has(s.value));
  });
  const mentionedAgent = ref2("");
  const contextKbs = ref2([]);
  const mentionedAgentObj = computed2(
    () => agentList.value.find((a) => a.agentCode === mentionedAgent.value) || null
  );
  const effectiveAgent = computed2(() => mentionedAgent.value || selectedAgent.value);
  const effectiveAgentObj = computed2(
    () => agentList.value.find((a) => a.agentCode === effectiveAgent.value) || null
  );
  const effectiveKnowledges = computed2(() => {
    const agent = effectiveAgentObj.value;
    const bound = [];
    if (agent && agent.knowledgeIds) {
      try {
        const parsed = typeof agent.knowledgeIds === "string" ? JSON.parse(agent.knowledgeIds) : agent.knowledgeIds;
        if (Array.isArray(parsed)) parsed.forEach((id) => bound.push(Number(id)));
      } catch (e) {
      }
    }
    const set = new Set(bound);
    return allKnowledges.value.filter((k) => set.has(Number(k.id)));
  });
  const kbScopeAll = ref2(true);
  const contextAll = computed2(() => kbScopeAll.value);
  function agentKbCount(agent) {
    if (!agent || !agent.knowledgeIds) return 0;
    try {
      const parsed = typeof agent.knowledgeIds === "string" ? JSON.parse(agent.knowledgeIds) : agent.knowledgeIds;
      return Array.isArray(parsed) ? parsed.length : 0;
    } catch (e) {
      return 0;
    }
  }
  async function loadAllOptions() {
    const rt = getAiRuntime();
    agentLoading.value = true;
    kbLoading.value = true;
    ragLoading.value = true;
    try {
      const res = rt.api.listAgent ? await rt.api.listAgent(
        options.agentQuery || { pageNum: 1, pageSize: -1, status: "0", lite: true }
      ) : null;
      const agentRows = res && res.rows || [];
      const visibleAgentRows = options.includeSystemAgents ? agentRows : agentRows.filter((a) => !isSystemAgent(a));
      agentList.value = visibleAgentRows.map((a) => ({
        agentCode: a.agentCode || a.id,
        id: a.id,
        agentName: a.agentName || "",
        agentDesc: a.agentDesc || "",
        agentAvatar: a.agentAvatar || "",
        agentType: a.agentType || "",
        modelCapabilities: a.modelCapabilities || "",
        // bindings used for the cascade filter
        knowledgeIds: a.knowledgeIds || "[]",
        ragType: a.ragType || ""
      }));
      registerAgentNames(agentRows);
      try {
        const kbRes = rt.api.listKnowledgeNoPage ? await rt.api.listKnowledgeNoPage() : null;
        allKnowledges.value = kbRes && (kbRes.rows || kbRes.data) || [];
      } catch (e) {
        allKnowledges.value = [];
      }
      try {
        const rsRes = rt.api.listRagStrategy ? await rt.api.listRagStrategy() : null;
        allRagStrategies.value = rsRes && (rsRes.data || rsRes.rows) || [];
      } catch (e) {
        allRagStrategies.value = [];
      }
      if (agentList.value.length > 0 && !selectedAgent.value) {
        const cachedAgent = rtGet(CHAT_CACHE_KEYS.agent);
        const matchedCached = cachedAgent ? agentList.value.find((a) => a.agentCode === cachedAgent) : null;
        selectedAgent.value = matchedCached ? matchedCached.agentCode : agentList.value[0].agentCode;
      }
      await enrichSelectedAgent();
      applyAgentDefaults();
    } catch (e) {
      if (rt.notify.error) rt.notify.error("\u52A0\u8F7D\u9009\u9879\u5931\u8D25");
    } finally {
      agentLoading.value = false;
      kbLoading.value = false;
      ragLoading.value = false;
    }
  }
  async function enrichSelectedAgent() {
    const rt = getAiRuntime();
    const agent = selectedAgentObj.value;
    if (!agent || !agent.id || !rt.api.getAgent) return;
    try {
      const detailRes = await rt.api.getAgent(agent.id);
      const detail = detailRes && detailRes.data;
      if (detail) {
        agent.knowledgeIds = detail.knowledgeIds || "[]";
        agent.ragType = detail.ragType || "";
        if (detail.modelCapabilities) agent.modelCapabilities = detail.modelCapabilities;
      }
    } catch (e) {
    }
  }
  function applyAgentDefaults() {
    kbScopeAll.value = true;
    contextKbs.value = effectiveKnowledges.value.map((k) => Number(k.id));
    const rss = filteredRagStrategies.value;
    const cachedRagType = rtGet(CHAT_CACHE_KEYS.ragType);
    const matchedRagType = cachedRagType ? rss.find((s) => s.value === cachedRagType) : null;
    if (matchedRagType) {
      selectedRagType.value = matchedRagType.value;
    } else {
      const hybridStrategy = rss.find((s) => s.value === "hybrid");
      selectedRagType.value = hybridStrategy ? hybridStrategy.value : rss.length > 0 ? rss[0].value : "";
    }
  }
  function setMentionedAgent(agentCode) {
    mentionedAgent.value = agentCode;
    selectedAgent.value = agentCode;
    kbScopeAll.value = true;
    contextKbs.value = effectiveKnowledges.value.map((k) => Number(k.id));
    if (agentCode) rtSet(CHAT_CACHE_KEYS.agent, agentCode);
    enrichSelectedAgent().then(() => {
      if (kbScopeAll.value) contextKbs.value = effectiveKnowledges.value.map((k) => Number(k.id));
    });
  }
  function clearMentionedAgent() {
    mentionedAgent.value = "";
    kbScopeAll.value = true;
    contextKbs.value = effectiveKnowledges.value.map((k) => Number(k.id));
  }
  function selectAllContextKbs() {
    kbScopeAll.value = true;
    contextKbs.value = effectiveKnowledges.value.map((k) => Number(k.id));
  }
  function toggleContextKb(kbId) {
    const id = Number(kbId);
    if (kbScopeAll.value) {
      kbScopeAll.value = false;
      contextKbs.value = [id];
      return;
    }
    const list = contextKbs.value;
    const next = list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
    if (next.length === 0) {
      selectAllContextKbs();
      return;
    }
    contextKbs.value = next;
  }
  function restoreContextKbs(ids) {
    const list = (ids || []).map(Number).filter((id) => !Number.isNaN(id));
    if (list.length === 0) {
      selectAllContextKbs();
      return;
    }
    const all = effectiveKnowledges.value.map((k) => Number(k.id));
    const isAll = all.length > 0 && all.length === list.length && all.every((id) => list.includes(id));
    kbScopeAll.value = isAll;
    contextKbs.value = list;
  }
  return {
    CHAT_CACHE_KEYS,
    agentList,
    selectedAgent,
    selectedRagType,
    agentLoading,
    kbLoading,
    ragLoading,
    allKnowledges,
    allRagStrategies,
    thinkingEnabled,
    handleThinkingChange,
    selectedAgentObj,
    agentSupportsImage,
    filteredRagStrategies,
    mentionedAgentObj,
    effectiveAgent,
    effectiveAgentObj,
    effectiveKnowledges,
    contextAll,
    agentKbCount,
    mentionedAgent,
    contextKbs,
    loadAllOptions,
    applyAgentDefaults,
    setMentionedAgent,
    clearMentionedAgent,
    selectAllContextKbs,
    toggleContextKb,
    restoreContextKbs,
    setOnAgentChanged
  };
}

// core/src/vue/useChatHistory.ts
import { ref as ref3, computed as computed3, reactive } from "vue";

// core/src/ai/history.ts
function restoreTraceToolChain(rawChain, depth = 1) {
  if (!Array.isArray(rawChain) || depth > 3) return [];
  return rawChain.map((raw, i) => {
    const r = raw && typeof raw === "object" ? raw : {};
    const trace = r.child_trace ?? r.childTrace;
    const node = {
      toolName: String(r.toolName ?? r.tool_name ?? ""),
      arguments: r.arguments,
      result: r.result,
      success: typeof r.success === "boolean" ? r.success : null,
      durationMs: typeof r.durationMs === "number" ? r.durationMs : typeof r.duration_ms === "number" ? r.duration_ms : 0,
      round: typeof r.round === "number" ? r.round : 0,
      pending: false,
      _expanded: false,
      _idx: i
    };
    if (typeof r.depth === "number") node.depth = r.depth;
    const code = r.agentCode ?? r.agent_code ?? (trace && trace.agent_code);
    if (code != null) node.agentCode = String(code);
    const rawChildren = r.children ?? (trace && (trace.tool_chain ?? trace.toolChain));
    const rawStages = r.childStages ?? r.child_stages ?? (trace && trace.stages);
    if (depth < 3 && Array.isArray(rawChildren)) {
      node.children = restoreTraceToolChain(rawChildren, depth + 1);
      node._childrenExpanded = false;
    }
    if (Array.isArray(rawStages)) {
      node.childStages = rawStages.map((s) => {
        const t = s && typeof s === "object" ? s : {};
        return {
          name: String(t.name ?? t.node_name ?? ""),
          status: "done",
          detail: t.detail != null ? String(t.detail) : "",
          durationMs: typeof t.durationMs === "number" ? t.durationMs : typeof t.duration_ms === "number" ? t.duration_ms : null
        };
      });
    }
    return node;
  });
}
function traceChainHasChildren(rawChain) {
  if (!Array.isArray(rawChain)) return false;
  return rawChain.some((t) => {
    if (!t || typeof t !== "object") return false;
    const r = t;
    return Array.isArray(r.children) || Array.isArray(r.childStages) || Array.isArray(r.child_stages) || !!(r.child_trace ?? r.childTrace);
  });
}

// core/src/vue/useChatHistory.ts
function useChatHistory(options = {}) {
  const rt = getAiRuntime();
  const {
    agentList = ref3([]),
    applyAgentDefaults = () => {
    },
    mentionedAgent = ref3(""),
    restoreContextKbs = () => {
    },
    selectedAgent = ref3(""),
    selectedRagType = ref3(""),
    messages = ref3([]),
    loading = ref3(false),
    inputText = ref3(""),
    nextMsgId = /* @__PURE__ */ (() => {
      let n = 0;
      return () => n++;
    })(),
    scrollToBottom = () => {
    },
    cancelStream = () => {
    },
    activeChatId = ref3(null),
    loadChatImagesFromStorage,
    parseWebSearchResults: parseWebSearchResults2,
    onQuickNewChat = () => {
    },
    pageSize = 100
  } = options;
  const historyList = ref3([]);
  const currentChatTitle = ref3("");
  const searchText = ref3("");
  const showFavoriteOnly = ref3(false);
  const projectName = ref3("");
  const projectLogo = ref3("");
  const selectedAction = ref3("new");
  const hasViewDeptPermission = ref3(false);
  const viewDeptEnabled = ref3(false);
  const renameVisible = ref3(false);
  const renameValue = ref3("");
  const renameTarget = ref3(null);
  const activeDropdown = ref3(null);
  function getCurrentProjectInfo() {
    try {
      const projects = JSON.parse(rtGet("projects") || "[]");
      const projectId = rtGet("projectId");
      let current = null;
      if (projects && projects.length > 0 && projectId) {
        current = projects.find((p) => p.projectId == projectId);
      }
      if (!current && projects && projects.length > 0) {
        current = projects[0];
      }
      if (current) {
        projectName.value = current.projectName || "AI \u52A9\u624B";
        if (current.projectLogo) {
          projectLogo.value = (rt.baseApi || "") + current.projectLogo;
        }
      } else {
        projectName.value = "AI \u52A9\u624B";
      }
    } catch (e) {
      projectName.value = "AI \u52A9\u624B";
    }
  }
  function handleSearch() {
    showFavoriteOnly.value = false;
    selectedAction.value = "";
  }
  function toggleFavoriteFilter() {
    showFavoriteOnly.value = !showFavoriteOnly.value;
    searchText.value = "";
    selectedAction.value = showFavoriteOnly.value ? "favorite" : "new";
  }
  function handleQuickNewChat() {
    handleNewChat();
    showFavoriteOnly.value = false;
    searchText.value = "";
    selectedAction.value = "new";
    onQuickNewChat();
  }
  function signalMemoryFlush(chatId) {
    if (!chatId) return;
    if (!rt.api.flushChatMemory) return;
    Promise.resolve(rt.api.flushChatMemory(chatId)).catch(() => {
    });
  }
  function initViewDeptPermission(permissions) {
    const perms = permissions || [];
    hasViewDeptPermission.value = perms.includes("huyuAI:chat:viewDept") || perms.includes("*:*:*");
    if (hasViewDeptPermission.value) {
      viewDeptEnabled.value = rtGet(CHAT_CACHE_KEYS.viewDept) === "true";
    }
  }
  function handleViewDeptChange(val) {
    viewDeptEnabled.value = val;
    rtSet(CHAT_CACHE_KEYS.viewDept, String(val));
    loadHistory();
  }
  const sortedHistoryList = computed3(() => {
    let list = [...historyList.value];
    if (showFavoriteOnly.value) {
      list = list.filter((item) => item.collectId && item.collectId > 0);
    }
    if (searchText.value.trim()) {
      const keyword = searchText.value.trim().toLowerCase();
      list = list.filter((item) => {
        const title = (item.questionOverview || item.questionContent || "").toLowerCase();
        return title.includes(keyword);
      });
    }
    list.sort((a, b) => {
      const aPinned = a.orderNum && a.orderNum > 0 ? 1 : 0;
      const bPinned = b.orderNum && b.orderNum > 0 ? 1 : 0;
      if (aPinned !== bPinned) return bPinned - aPinned;
      if (aPinned && bPinned) return (b.orderNum || 0) - (a.orderNum || 0);
      return 0;
    });
    return list;
  });
  const expandedGroups = ref3(/* @__PURE__ */ new Set(["\u4ECA\u5929", "3\u5929\u5185"]));
  function isToday(date) {
    const now = /* @__PURE__ */ new Date();
    return date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth() && date.getDate() === now.getDate();
  }
  function isYesterday(date) {
    const yesterday = /* @__PURE__ */ new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return date.getFullYear() === yesterday.getFullYear() && date.getMonth() === yesterday.getMonth() && date.getDate() === yesterday.getDate();
  }
  function getTimeGroup(createTime) {
    if (!createTime) return "\u66F4\u65E9";
    const date = new Date(createTime);
    const now = /* @__PURE__ */ new Date();
    const diffTime = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffTime / (1e3 * 60 * 60 * 24));
    if (isToday(date)) return "\u4ECA\u5929";
    if (isYesterday(date)) return "\u6628\u5929";
    if (diffDays <= 3) return "3\u5929\u5185";
    if (diffDays <= 7) return "7\u5929\u5185";
    if (diffDays <= 30) return "\u4E00\u4E2A\u6708\u5185";
    return `${date.getFullYear()}\u5E74${date.getMonth() + 1}\u6708`;
  }
  function toggleGroup(groupName) {
    if (expandedGroups.value.has(groupName)) {
      expandedGroups.value.delete(groupName);
    } else {
      expandedGroups.value.add(groupName);
    }
  }
  const groupedHistoryList = computed3(() => {
    const list = sortedHistoryList.value;
    const groupOrder = ["\u4ECA\u5929", "\u6628\u5929", "3\u5929\u5185", "7\u5929\u5185", "\u4E00\u4E2A\u6708\u5185"];
    const groups = {};
    const pinnedItems = [];
    const unpinnedItems = [];
    list.forEach((item) => {
      if (item.orderNum && item.orderNum > 0) {
        pinnedItems.push(item);
      } else {
        unpinnedItems.push(item);
      }
    });
    unpinnedItems.forEach((item) => {
      const group = getTimeGroup(item.createTime);
      if (!groups[group]) {
        groups[group] = [];
      }
      groups[group].push(item);
    });
    const result = [];
    if (pinnedItems.length > 0) {
      result.push({
        label: "\u7F6E\u9876",
        count: pinnedItems.length,
        expanded: true,
        items: pinnedItems
      });
    }
    groupOrder.forEach((groupName) => {
      if (groups[groupName] && groups[groupName].length > 0) {
        result.push({
          label: groupName,
          count: groups[groupName].length,
          expanded: expandedGroups.value.has(groupName),
          items: groups[groupName]
        });
        delete groups[groupName];
      }
    });
    const monthGroups = Object.keys(groups).filter((g) => g.includes("\u5E74") && g.includes("\u6708")).sort((a, b) => b.localeCompare(a));
    monthGroups.forEach((groupName) => {
      result.push({
        label: groupName,
        count: groups[groupName].length,
        expanded: expandedGroups.value.has(groupName),
        items: groups[groupName]
      });
    });
    if (groups["\u66F4\u65E9"] && groups["\u66F4\u65E9"].length > 0) {
      result.push({
        label: "\u66F4\u65E9",
        count: groups["\u66F4\u65E9"].length,
        expanded: expandedGroups.value.has("\u66F4\u65E9"),
        items: groups["\u66F4\u65E9"]
      });
    }
    return result;
  });
  function toReactiveMessages(list) {
    return list.map((m) => reactive(m));
  }
  async function loadHistory() {
    if (!rt.api.getListByUserApi) return;
    try {
      const query = { pageNum: 1, pageSize, recordType: "conversation" };
      if (viewDeptEnabled.value && hasViewDeptPermission.value) {
        query.viewDept = true;
      }
      const res = await rt.api.getListByUserApi(query);
      historyList.value = res && res.rows || [];
    } catch (e) {
    }
  }
  async function saveSession(_question, _answer) {
    if (!activeChatId.value) {
      try {
        await loadHistory();
      } catch (e) {
      }
      return;
    }
    const selfChatId = String(activeChatId.value);
    const MAX_ATTEMPTS = 8;
    const RETRY_MS = 900;
    for (let attempts = 0; attempts < MAX_ATTEMPTS; attempts++) {
      try {
        await loadHistory();
      } catch (e) {
      }
      const exists = historyList.value.some((i) => String(i.chatId) === selfChatId);
      if (exists) break;
      if (attempts < MAX_ATTEMPTS - 1) {
        await new Promise((resolve2) => setTimeout(resolve2, RETRY_MS));
      }
    }
  }
  async function loadHistoryItem(item) {
    if (loading.value) {
      cancelStream();
    }
    signalMemoryFlush(activeChatId.value);
    selectedAction.value = "";
    showFavoriteOnly.value = false;
    searchText.value = "";
    activeChatId.value = item.chatId;
    currentChatTitle.value = item.questionOverview || item.questionContent || "";
    messages.value = [];
    mentionedAgent.value = "";
    const historyAgentCode = item.agentCode || item.agentId;
    if (historyAgentCode) {
      const matchedAgent = agentList.value.find(
        (a) => a.agentCode === historyAgentCode || a.id === historyAgentCode
      );
      if (matchedAgent) {
        selectedAgent.value = matchedAgent.agentCode;
        rtSet(CHAT_CACHE_KEYS.agent, matchedAgent.agentCode);
        applyAgentDefaults();
      }
    }
    if (item.knowledgeIds) {
      try {
        const parsed = typeof item.knowledgeIds === "string" ? JSON.parse(item.knowledgeIds) : item.knowledgeIds;
        if (Array.isArray(parsed) && parsed.length > 0) {
          restoreContextKbs(parsed);
        }
      } catch (e) {
      }
    }
    if (item.ragType) {
      selectedRagType.value = item.ragType;
    }
    try {
      const res = rt.api.getHistoryChatApi ? await rt.api.getHistoryChatApi(item.chatId, { recordType: "conversation" }) : null;
      const messagesList = res && res.data && res.data.messages || [];
      const rebuilt = [];
      const cachedImages = typeof loadChatImagesFromStorage === "function" ? loadChatImagesFromStorage(item.chatId) : [];
      let userImgCursor = 0;
      for (const msg of messagesList) {
        if (msg.role === "user") {
          const backendImages = Array.isArray(msg.images) ? msg.images.filter((u) => typeof u === "string" && u) : [];
          const restoredImage = userImgCursor < cachedImages.length && backendImages.length === 0 ? cachedImages[userImgCursor++] : void 0;
          if (backendImages.length > 0) {
            userImgCursor = Math.min(userImgCursor + backendImages.length, cachedImages.length);
          }
          rebuilt.push({
            _id: nextMsgId(),
            role: "user",
            content: msg.content,
            image: restoredImage,
            images: backendImages.length > 0 ? backendImages : void 0,
            createTime: msg.timestamp || ""
          });
        } else {
          const toolCalls = (msg.toolCalls || []).map((tc) => ({
            toolName: tc.toolName,
            arguments: tc.functionArgs,
            result: tc.toolResult,
            success: tc.callStatus === "success",
            durationMs: tc.callDurationMs || 0
          }));
          const parsedWs = parseWebSearchResults2 ? parseWebSearchResults2(toolCalls) : { results: [], optimizedQuery: "" };
          const webSearchResults = parsedWs.results || [];
          const optimizedSearchQuery = parsedWs.optimizedQuery || "";
          const knowledgeTraces = (msg.knowledgeTraces || []).map((trace2) => ({
            index: trace2.index,
            knowledgeId: trace2.knowledgeId,
            qaId: trace2.qaId,
            // 溯源可跳转 ID：缺失会导致历史会话里「查看原文 / 源数据」点不开
            documentId: trace2.documentId,
            chunkId: trace2.chunkId,
            documentName: trace2.documentName,
            question: trace2.question,
            answer: trace2.answer,
            confidence: trace2.confidence,
            confidenceLevel: trace2.confidenceLevel,
            knowledgeName: trace2.knowledgeName,
            category: trace2.category,
            keywords: trace2.keywords,
            source: trace2.source,
            matchSource: trace2.matchSource,
            images: trace2.images || [],
            matchCount: trace2.matchCount,
            vectorScore: trace2.vectorScore,
            rrfRank: trace2.rrfRank,
            searchDurationMs: trace2.searchDurationMs,
            searchMethod: trace2.searchMethod,
            vectorWeight: trace2.vectorWeight,
            bm25Weight: trace2.bm25Weight,
            graphWeight: trace2.graphWeight
          }));
          const toolChain = toolCalls.map((tc, i) => ({
            ...tc,
            _expanded: false,
            _idx: i
          }));
          const trace = msg.trace || null;
          const traceStages = Array.isArray(trace?.stages) ? trace.stages.map((s) => ({
            name: s.name,
            // 历史里不存在"进行中"，running 一律按已完成呈现
            status: s.status === "error" ? "error" : "done",
            detail: s.detail || "",
            durationMs: s.durationMs ?? null
          })) : [];
          const traceChainRaw = Array.isArray(trace?.toolChain) ? trace.toolChain : Array.isArray(trace?.tool_chain) ? trace.tool_chain : null;
          const traceChain = traceChainHasChildren(traceChainRaw) ? restoreTraceToolChain(traceChainRaw) : Array.isArray(trace?.toolChain) && trace.toolChain.length > 0 ? trace.toolChain.map((t, i) => ({
            toolName: t.toolName,
            arguments: t.arguments,
            result: t.result,
            success: t.success,
            durationMs: t.durationMs || 0,
            round: t.round ?? 0,
            pending: false,
            _expanded: false,
            _idx: i
          })) : toolChain;
          const traceNotices = Array.isArray(trace?.notices) ? trace.notices.filter((n) => typeof n === "string" && n.trim()) : [];
          rebuilt.push({
            _id: nextMsgId(),
            role: "assistant",
            content: msg.content,
            createTime: msg.timestamp || "",
            usage: null,
            // 中断还原：后端落库时标了 TRUNCATED / cancelled，历史里也要显示成"中断"，
            // 否则用户看到一段突然断掉的答案却没有任何说明。
            // max_tokens 单独成一态：它是正常收尾但撞了长度上限，提示文案与 interrupted 不同，
            // 但同样要给「继续生成」；老数据没有 finishStatus 只有 cancelled，按 interrupted 兜底。
            cancelled: false,
            interrupted: trace?.finishStatus === "interrupted" || trace?.cancelled === true && trace?.finishStatus !== "max_tokens",
            truncatedByLength: trace?.finishStatus === "max_tokens",
            reasoning: msg.thinkingContent || "",
            _reasoningExpanded: true,
            toolCalls,
            toolChain: traceChain,
            stages: traceStages,
            notices: traceNotices,
            webSearchResults: webSearchResults.length > 0 ? webSearchResults : null,
            optimizedSearchQuery: optimizedSearchQuery || "",
            knowledgeTraces: knowledgeTraces.length > 0 ? knowledgeTraces : null,
            _loopExpanded: false,
            _renderKey: 0
          });
        }
      }
      messages.value = toReactiveMessages(rebuilt);
    } catch (e) {
      const rebuilt = [];
      if (item.questionContent) {
        const cachedFirstImage = typeof loadChatImagesFromStorage === "function" ? loadChatImagesFromStorage(item.chatId)[0] || void 0 : void 0;
        rebuilt.push({
          _id: nextMsgId(),
          role: "user",
          content: item.questionContent,
          image: cachedFirstImage,
          createTime: item.createTime || ""
        });
      }
      if (item.fields1) {
        rebuilt.push({
          _id: nextMsgId(),
          role: "assistant",
          content: item.fields1,
          createTime: item.createTime || "",
          usage: null,
          cancelled: false,
          _renderKey: 0
        });
      }
      messages.value = toReactiveMessages(rebuilt);
    } finally {
      scrollToBottom();
    }
  }
  function handleNewChat() {
    signalMemoryFlush(activeChatId.value);
    const wasStreaming = loading.value;
    if (wasStreaming) {
      cancelStream();
      loadHistory();
    }
    messages.value = [];
    activeChatId.value = null;
    currentChatTitle.value = "";
    inputText.value = "";
    loading.value = false;
  }
  function handleClear() {
    if (messages.value.length === 0) return;
    const doClear = () => {
      signalMemoryFlush(activeChatId.value);
      cancelStream();
      messages.value = [];
      activeChatId.value = null;
      currentChatTitle.value = "";
      loading.value = false;
    };
    if (rt.notify.confirm) {
      rt.notify.confirm("\u786E\u5B9A\u6E05\u7A7A\u5F53\u524D\u5BF9\u8BDD\u5417\uFF1F", "\u63D0\u793A", { type: "warning" }).then(doClear).catch(() => {
      });
    } else {
      doClear();
    }
  }
  function startRename(item) {
    renameTarget.value = item;
    renameValue.value = item.questionOverview || item.questionContent || "";
    renameVisible.value = true;
  }
  async function confirmRename() {
    if (!renameValue.value.trim() || !renameTarget.value) {
      renameVisible.value = false;
      return;
    }
    try {
      if (rt.api.updateChat) {
        await rt.api.updateChat({
          chatId: renameTarget.value.chatId,
          questionOverview: renameValue.value.trim()
        });
      }
      renameVisible.value = false;
      await loadHistory();
    } catch (e) {
      if (rt.notify.error) rt.notify.error("\u91CD\u547D\u540D\u5931\u8D25");
    }
  }
  async function toggleFavorite(item) {
    const newCollectId = item.collectId && item.collectId > 0 ? 0 : 1;
    try {
      if (rt.api.updateChat) await rt.api.updateChat({ chatId: item.chatId, collectId: newCollectId });
      await loadHistory();
    } catch (e) {
      if (rt.notify.error) rt.notify.error("\u64CD\u4F5C\u5931\u8D25");
    }
  }
  async function togglePin(item) {
    const newOrderNum = item.orderNum && item.orderNum > 0 ? 0 : 999;
    try {
      if (rt.api.updateChat) await rt.api.updateChat({ chatId: item.chatId, orderNum: newOrderNum });
      await loadHistory();
    } catch (e) {
      if (rt.notify.error) rt.notify.error("\u64CD\u4F5C\u5931\u8D25");
    }
  }
  async function handleDelete(item) {
    if (rt.notify.confirm) {
      try {
        await rt.notify.confirm("\u786E\u5B9A\u8981\u5220\u9664\u8BE5\u4F1A\u8BDD\u5417\uFF1F\u5220\u9664\u540E\u4E0D\u53EF\u6062\u590D\u3002", "\u5220\u9664\u786E\u8BA4", {
          type: "warning"
        });
      } catch (e) {
        return;
      }
    }
    signalMemoryFlush(item.chatId);
    try {
      if (rt.api.deleteChatApi) await rt.api.deleteChatApi(item.chatId);
      if (activeChatId.value === item.chatId) {
        messages.value = [];
        activeChatId.value = null;
      }
      await loadHistory();
      if (rt.notify.success) rt.notify.success("\u5220\u9664\u6210\u529F");
    } catch (e) {
      if (rt.notify.error) rt.notify.error("\u5220\u9664\u5931\u8D25");
    }
  }
  function toggleDropdown(chatId) {
    activeDropdown.value = activeDropdown.value === chatId ? null : chatId;
  }
  function closeDropdown() {
    activeDropdown.value = null;
  }
  return {
    // 状态
    historyList,
    activeChatId,
    currentChatTitle,
    searchText,
    showFavoriteOnly,
    projectName,
    projectLogo,
    selectedAction,
    hasViewDeptPermission,
    viewDeptEnabled,
    renameVisible,
    renameValue,
    renameTarget,
    activeDropdown,
    expandedGroups,
    // computed
    sortedHistoryList,
    groupedHistoryList,
    // 方法
    getCurrentProjectInfo,
    handleSearch,
    toggleFavoriteFilter,
    handleQuickNewChat,
    initViewDeptPermission,
    handleViewDeptChange,
    toggleGroup,
    loadHistory,
    saveSession,
    loadHistoryItem,
    handleNewChat,
    handleClear,
    startRename,
    confirmRename,
    toggleFavorite,
    togglePin,
    handleDelete,
    toggleDropdown,
    closeDropdown,
    // 会话结束记忆沉淀信号
    signalMemoryFlush
  };
}

// core/src/vue/useChatFaq.ts
import { ref as ref4 } from "vue";
function useChatFaq(options = {}) {
  const rt = getAiRuntime();
  const { selectedAgentObj = ref4(null), handleSend = () => {
  }, inputText = ref4("") } = options;
  const faqTreeData = ref4([]);
  const faqTreeLoading = ref4(false);
  const quickQuestions = ref4([]);
  const defaultCardStyle = [
    { icon: null, color: "#409eff" },
    { icon: null, color: "#67c23a" },
    { icon: null, color: "#909399" },
    { icon: null, color: "#e6a23c" },
    { icon: null, color: "#409eff" }
  ];
  const quickCardStyle = rt.quickCardStyle && rt.quickCardStyle.length > 0 ? rt.quickCardStyle : defaultCardStyle;
  let quickReqSeq = 0;
  async function loadQuickQuestions() {
    if (!rt.api.treeListFaq) {
      quickQuestions.value = [];
      return;
    }
    const seq = ++quickReqSeq;
    try {
      const query = { isVisible: "1", fields1: "quick" };
      const agentId = selectedAgentObj.value && selectedAgentObj.value.id;
      if (agentId) query.agentId = agentId;
      const res = await rt.api.treeListFaq(query);
      if (seq !== quickReqSeq) return;
      const list = res && res.data || [];
      const sorted = [...list].sort((a, b) => {
        const aAgent = !!(a.fields2 && String(a.fields2).trim() !== "");
        const bAgent = !!(b.fields2 && String(b.fields2).trim() !== "");
        return (bAgent ? 1 : 0) - (aAgent ? 1 : 0);
      });
      quickQuestions.value = sorted.map((item, index) => {
        const style = quickCardStyle[index % quickCardStyle.length];
        const isAgentSpecific = !!(item.fields2 && String(item.fields2).trim() !== "");
        return {
          // question：卡片上展示的简短关键词
          question: item.faqName,
          // prompt：点击后实际发送的内容（提示词为空时回退关键词）
          prompt: item.faqContent || item.faqName,
          icon: style.icon,
          color: style.color,
          scope: isAgentSpecific ? "agent" : "general"
        };
      });
    } catch (e) {
      if (seq === quickReqSeq) quickQuestions.value = [];
    }
  }
  let treeReqSeq = 0;
  async function loadFaqTree(opts = {}) {
    if (!rt.api.treeListFaq) return;
    const seq = ++treeReqSeq;
    faqTreeLoading.value = true;
    try {
      const query = { isVisible: "1" };
      if (opts.excludeQuick) query.excludeQuick = true;
      const agentId = selectedAgentObj.value && selectedAgentObj.value.id;
      if (opts.byAgent && agentId) query.agentId = agentId;
      const res = await rt.api.treeListFaq(query);
      if (seq !== treeReqSeq) return;
      faqTreeData.value = res && res.data || [];
    } catch (e) {
      if (seq === treeReqSeq) faqTreeData.value = [];
    } finally {
      if (seq === treeReqSeq) faqTreeLoading.value = false;
    }
  }
  function sendQuickQuestion(prompt) {
    if (!prompt) return;
    inputText.value = prompt;
    handleSend();
  }
  return {
    quickQuestions,
    quickCardStyle,
    faqTreeData,
    faqTreeLoading,
    loadQuickQuestions,
    loadFaqTree,
    sendQuickQuestion
  };
}

// core/src/vue/useChatRender.ts
import { ref as ref5, computed as computed4 } from "vue";
function tryParseAssessmentJson(content) {
  if (!content || !content.trim()) return null;
  const trimmed = content.trim();
  if (trimmed.indexOf("{") !== 0) return null;
  try {
    const parsed = JSON.parse(trimmed);
    if (parsed && typeof parsed === "object" && parsed.coverage && parsed.aging && typeof parsed.summary === "string") {
      return parsed;
    }
  } catch (e) {
  }
  return null;
}
function assessmentJsonToMarkdown(d) {
  const lines = [];
  const coverage = d.coverage || {};
  const aging = d.aging || {};
  const species = d.species || {};
  const alerts = Array.isArray(d.alerts) ? d.alerts : [];
  const suggestion = Array.isArray(d.suggestion) ? d.suggestion : [];
  lines.push("\u{1F4CA} \u6C34\u8349\u72B6\u6001\u7EFC\u5408\u8BC4\u4F30");
  lines.push("");
  lines.push("\u2460 \u6C34\u8349\u8986\u76D6\u7387");
  lines.push("| \u6307\u6807 | \u8BC4\u4F30\u7ED3\u679C |");
  lines.push("| --- | --- |");
  lines.push(`| \u89C6\u89C9\u8986\u76D6 | ${coverage.coverage_pct || "\u2014"} |`);
  lines.push(`| \u5206\u7EA7\u5224\u5B9A | ${coverage.coverage_status || "\u2014"} |`);
  lines.push(`| \u5206\u5858\u7A7A\u9699 | ${coverage.gap_status || "\u2014"} |`);
  if (coverage.evidence) {
    lines.push("");
    lines.push(`> \u4F9D\u636E\uFF1A${coverage.evidence}`);
  }
  lines.push("");
  lines.push("\u2461 \u8001\u5316\u7A0B\u5EA6\u8BC4\u4F30");
  lines.push("| \u6307\u6807 | \u8BC4\u4F30\u7ED3\u679C |");
  lines.push("| --- | --- |");
  lines.push(`| \u8001\u5316\u7B49\u7EA7 | ${aging.aging_status || "\u2014"} |`);
  const rotPct = aging.rot_ratio_pct != null ? aging.rot_ratio_pct + "%" : "\u2014";
  lines.push(`| \u8150\u70C2\u5360\u6BD4 | ${rotPct} |`);
  lines.push(`| \u9752\u82D4/\u4E1D\u72B6\u85FB\u9644\u7740 | ${aging.slime_attachment ? "\u6709" : "\u65E0"} |`);
  if (aging.evidence) {
    lines.push("");
    lines.push(`> \u4F9D\u636E\uFF1A${aging.evidence}`);
  }
  lines.push("");
  lines.push("\u2462 \u6C34\u8349\u79CD\u7C7B\u8BC6\u522B");
  lines.push("| \u6307\u6807 | \u8BC4\u4F30\u7ED3\u679C |");
  lines.push("| --- | --- |");
  const speciesList = Array.isArray(species.species) ? species.species.join("\u3001") : "\u2014";
  lines.push(`| \u8BC6\u522B\u79CD\u7C7B | ${speciesList} |`);
  lines.push(`| \u4F18\u52BF\u79CD | ${species.dominant || "\u2014"} |`);
  lines.push(`| \u7F6E\u4FE1\u5EA6 | ${species.confidence || "\u2014"} |`);
  lines.push("");
  lines.push("\u2463 \u5F02\u5E38\u9884\u8B66 / \u9644\u7740\u8FF9\u8C61");
  if (alerts.length === 0) {
    lines.push("- \u672A\u53D1\u73B0\u660E\u663E\u5F02\u5E38");
  } else {
    const levelMap = { critical: "\u{1F534} \u4E25\u91CD", warn: "\u{1F7E1} \u8B66\u544A", info: "\u{1F535} \u63D0\u793A" };
    alerts.forEach((a) => {
      const tag = levelMap[a.level] || "\u26AA \u63D0\u793A";
      lines.push(`- ${tag} **${a.issue}**${a.message ? "\uFF1A" + a.message : ""}`);
    });
  }
  lines.push("");
  lines.push("\u{1F4CB} \u5904\u7F6E\u5EFA\u8BAE");
  const priMap = { \u9AD8: "\u{1F534} \u9AD8", \u4E2D: "\u{1F7E1} \u4E2D", \u4F4E: "\u{1F7E2} \u4F4E" };
  if (suggestion.length === 0) {
    lines.push("- \u7EF4\u6301\u5F53\u524D\u5E38\u89C4\u7BA1\u7406\u5373\u53EF");
  } else {
    suggestion.forEach((s) => {
      const pri = priMap[s.priority] || "\u{1F7E2} \u4F4E";
      lines.push(`- **${pri}** ${s.issue}${s.action ? " \u2192 " + s.action : ""}`);
    });
  }
  lines.push("");
  lines.push(`**\u7ED3\u8BBA\uFF1A** ${d.summary}`);
  return lines.join("\n");
}
function renderMessageHtml(msg) {
  const rt = getAiRuntime();
  if (!msg._renderKey) msg._renderKey = 0;
  let content = msg.content || "";
  const jsonAssess = !msg.streaming ? tryParseAssessmentJson(content) : null;
  if (jsonAssess) {
    content = assessmentJsonToMarkdown(jsonAssess);
  }
  if (getReportCards(msg).length > 0) {
    let cleaned = content.replace(/\*\*([^*]+)\*\*/g, "$1");
    cleaned = cleaned.replace(/^[^\n]*链接[：:]\s*https?:\/\/\S+[^\n]*$/gm, "");
    cleaned = cleaned.replace(/^[^\n]*已生成[^\n]*https?:\/\/\S+[^\n]*$/gm, "");
    cleaned = cleaned.replace(/^[^\n]*https?:\/\/[^\n]*profile\/report[^\n]*$/gm, "");
    cleaned = cleaned.replace(/^[^\n]*\[[^\]]*\]\(https?:\/\/[^)]+\)[^\n]*$/gm, "");
    cleaned = cleaned.replace(/\n{3,}/g, "\n\n").trim();
    content = cleaned;
  }
  if (msg.knowledgeTraces && msg.knowledgeTraces.length > 0) {
    content = content.replace(/\[(\d+)\]/g, (match, index) => {
      const traceIndex = parseInt(index, 10) - 1;
      if (traceIndex >= 0 && traceIndex < msg.knowledgeTraces.length) {
        return `@@CITE_${traceIndex}@@`;
      }
      return match;
    });
  }
  let html = rt.renderMarkdown(content);
  if (msg.knowledgeTraces && msg.knowledgeTraces.length > 0) {
    html = html.replace(/@@CITE_(\d+)@@/g, (match, traceIndexStr) => {
      const traceIndex = parseInt(traceIndexStr, 10);
      const trace = msg.knowledgeTraces[traceIndex];
      if (trace) {
        const levelClass = "citation-" + (trace.confidenceLevel || "medium");
        const safeTitle = escapeHtml(trace.question || "") + " / \u7B54\u6848: " + escapeHtml(trace.answer || "") + " / \u7F6E\u4FE1\u5EA6: " + ((trace.confidence || 0) * 100).toFixed(1) + "%";
        return `<span class="citation-badge ${levelClass}" data-trace-index="${traceIndex}" data-chat-msg-id="${msg._id || ""}" title="${safeTitle}">${traceIndex + 1}</span>`;
      }
      return match;
    });
  }
  return html;
}
function useChatRender(options = {}) {
  const rt = getAiRuntime();
  const {
    messages = ref5([]),
    router = rt.router || null,
    knowledgeRoute = "/huyuAI/AI/knowledge"
  } = options;
  function toggleReasoningExpand(msg) {
    msg._reasoningExpanded = !msg._reasoningExpanded;
    bumpRenderKey(msg);
    messages.value = [...messages.value];
  }
  function toggleToolChainNode(msg, index) {
    const item = msg.toolChain && msg.toolChain.find((tc) => tc._idx === index);
    if (item) item._expanded = !item._expanded;
    bumpRenderKey(msg);
    messages.value = [...messages.value];
  }
  function toggleToolChainGroup(msg, round) {
    if (!msg._groupExpanded) msg._groupExpanded = {};
    msg._groupExpanded[round] = !isToolChainGroupExpanded(msg, round);
    bumpRenderKey(msg);
    messages.value = [...messages.value];
  }
  const tracePanelVisible = ref5(false);
  const currentTraceMsg = ref5(null);
  const activeTraceTab = ref5("qa");
  const activeTraceIndex = ref5(0);
  const traceSelectorVisible = ref5(false);
  const traceSelectorMsg = ref5(null);
  const qaTraces = computed4(
    () => (currentTraceMsg.value?.knowledgeTraces || []).filter((t) => !t.source || t.source === "qa")
  );
  const docTraces = computed4(
    () => (currentTraceMsg.value?.knowledgeTraces || []).filter((t) => t.source === "doc")
  );
  const graphTraces = computed4(
    () => (currentTraceMsg.value?.knowledgeTraces || []).filter((t) => t.source === "graph")
  );
  function openTracePanel(msg, traceIdx) {
    currentTraceMsg.value = msg;
    const idx = traceIdx ?? 0;
    const trace = msg?.knowledgeTraces?.[idx];
    if (trace?.source === "doc") {
      activeTraceTab.value = "doc";
    } else if (trace?.source === "graph") {
      activeTraceTab.value = "graph";
    } else {
      activeTraceTab.value = "qa";
    }
    activeTraceIndex.value = idx;
    tracePanelVisible.value = true;
  }
  function handleViewTrace(msg) {
    if (!msg?.knowledgeTraces?.length) return;
    if (msg.knowledgeTraces.length === 1) {
      openTracePanel(msg, 0);
    } else {
      traceSelectorMsg.value = msg;
      traceSelectorVisible.value = true;
    }
  }
  function selectTrace(msg, idx) {
    traceSelectorVisible.value = false;
    openTracePanel(msg, idx);
  }
  function handleNavigateToKnowledge(trace) {
    if (!trace?.knowledgeId || !router) return;
    router.push({ path: knowledgeRoute, query: { selectId: trace.knowledgeId, highlightQaId: trace.qaId } });
  }
  function handleViewSourceData(trace) {
    if (!trace?.knowledgeId || !router) return;
    router.push({ path: knowledgeRoute, query: { selectId: trace.knowledgeId, highlightQaId: trace.qaId } });
  }
  function handleViewCiteDocument(trace) {
    if (!trace?.documentId || !router) return;
    router.push({
      path: knowledgeRoute,
      query: {
        selectId: trace.knowledgeId || void 0,
        docId: trace.documentId,
        docName: trace.documentName || void 0
      }
    });
  }
  function handleCitationClick(e) {
    const target = e.target.closest(".citation-badge");
    if (!target) return;
    e.stopPropagation();
    const traceIndex = parseInt(target.getAttribute("data-trace-index"), 10);
    const chatMsgId = target.getAttribute("data-chat-msg-id");
    const msg = messages.value.find((m) => m._id === chatMsgId);
    if (!msg || !msg.knowledgeTraces) return;
    openTracePanel(msg, traceIndex);
  }
  function toggleWebSearchExpand(msg) {
    if (!msg) return;
    msg._webSearchExpanded = !msg._webSearchExpanded;
    bumpRenderKey(msg);
    messages.value = [...messages.value];
  }
  function toggleStagePanel(msg) {
    if (!msg) return;
    msg._stagePanelExpanded = !isStagePanelExpanded(msg);
    bumpRenderKey(msg);
    messages.value = [...messages.value];
  }
  function toggleTurnCard(msg) {
    if (!msg) return;
    msg._turnCardExpanded = !isTurnCardExpanded(msg);
    bumpRenderKey(msg);
    messages.value = [...messages.value];
  }
  return {
    // 工具函数（纯函数，模块级导出，组件可直接 import）
    currentTime,
    truncate,
    formatToolArguments,
    formatToolResult,
    isWebSearchTool,
    isReportTool,
    getReportCards,
    openReportUrl,
    getWebSearchQuery,
    hasNonWebSearchTools,
    parseWebSearchResults,
    renderMessageHtml,
    escapeHtml,
    // 工具链展示
    groupToolChainByRound,
    getGroupTitle,
    parseReasoningRounds,
    getToolDisplayName,
    toggleReasoningExpand,
    toggleToolChainNode,
    toggleToolChainGroup,
    isToolChainGroupExpanded,
    // 溯源面板
    tracePanelVisible,
    currentTraceMsg,
    activeTraceTab,
    activeTraceIndex,
    traceSelectorVisible,
    traceSelectorMsg,
    qaTraces,
    docTraces,
    graphTraces,
    openTracePanel,
    handleViewTrace,
    selectTrace,
    overallConfidenceValue,
    overallConfidenceLevel,
    getHitImages,
    normalizeImageUrl,
    getFeedRecordImages,
    confidenceTagType,
    confidenceLabel,
    matchSourceLabel,
    handleNavigateToKnowledge,
    handleViewSourceData,
    handleCitationClick,
    handleViewCiteDocument,
    // Web 搜索展开
    isWebSearchExpanded,
    toggleWebSearchExpand,
    // 过程时间线展开
    getStageDisplayName,
    hasRunningStage,
    stagePanelSummary,
    isStagePanelExpanded,
    summarizeToolOutcome,
    toolFlowSummary,
    toggleStagePanel,
    // 回合卡片（14 阶段归并 5 个动作）
    buildTurnActions,
    turnCardSummary,
    isTurnCardExpanded,
    toggleTurnCard
  };
}

// core/src/vue/useVoiceChat.ts
import { ref as ref6, watch as watch3, onMounted, onBeforeUnmount } from "vue";
var DEFAULT_KEYWORD = "\u4F60\u597D\u864E\u9C7C";
function useVoiceChat(options = {}) {
  const rt = getAiRuntime();
  const {
    storageKey = "ai_chat_speech_settings",
    onWakeEnter = () => {
    },
    onVoiceResult = () => {
    },
    isUiActive = () => false,
    isRecordBlocked = () => false,
    mountReady = () => true
  } = options;
  const notify = {
    success: (m) => rt.notify.success && rt.notify.success(m),
    error: (m) => rt.notify.error && rt.notify.error(m),
    warning: (m) => rt.notify.warning && rt.notify.warning(m)
  };
  const loadSavedSettings = () => {
    try {
      return JSON.parse(rtGet(storageKey) || "{}") || {};
    } catch {
      return {};
    }
  };
  const savedSettings = loadSavedSettings();
  const kwsEnabled = ref6(savedSettings.kwsEnabled === true);
  const kwsKeywords = ref6(
    /^[\u4e00-\u9fff]+$/.test(savedSettings.kwsKeywords || "") ? savedSettings.kwsKeywords : DEFAULT_KEYWORD
  );
  const settingsVisible = ref6(false);
  const saveKwsSetting = () => {
    try {
      const cur = loadSavedSettings();
      cur.kwsEnabled = !!kwsEnabled.value;
      cur.kwsKeywords = kwsKeywords.value || DEFAULT_KEYWORD;
      rtSet(storageKey, JSON.stringify(cur));
    } catch {
    }
  };
  const ttsEngine = ref6(savedSettings.ttsEngine === "edgetts" ? "edgetts" : "local");
  const ttsVoice = ref6(savedSettings.ttsVoice || "zh-CN-XiaoxiaoNeural");
  const edgeTtsVoices = ref6([]);
  const saveTtsSetting = () => {
    try {
      const cur = loadSavedSettings();
      cur.ttsEngine = ttsEngine.value === "edgetts" ? "edgetts" : "local";
      cur.ttsVoice = ttsVoice.value || "zh-CN-XiaoxiaoNeural";
      rtSet(storageKey, JSON.stringify(cur));
    } catch {
    }
  };
  const loadSpeechConfig = async () => {
    if (!rt.api.getSpeechConfig) return;
    try {
      const res = await rt.api.getSpeechConfig();
      const data = res && res.data || res || {};
      if (!kwsKeywords.value || kwsKeywords.value === DEFAULT_KEYWORD) {
        kwsKeywords.value = data.kwsKeywords || DEFAULT_KEYWORD;
      }
      if (data.edgeTtsVoices && Array.isArray(data.edgeTtsVoices) && data.edgeTtsVoices.length > 0) {
        edgeTtsVoices.value = data.edgeTtsVoices;
      }
      if (data.edgeTtsDefaultVoice && !savedSettings.ttsVoice) {
        ttsVoice.value = data.edgeTtsDefaultVoice;
      }
    } catch (e) {
    }
  };
  const kwsListening = ref6(false);
  const voiceFromWake = ref6(false);
  const handleKwsWake = (keyword) => {
    notify.success("\u5DF2\u5524\u9192\uFF1A" + keyword);
    kwsListening.value = false;
    voiceFromWake.value = true;
    onWakeEnter(keyword);
  };
  const handleKwsError = (e) => {
    kwsListening.value = false;
    notify.error("\u5524\u9192\u76D1\u542C\u5931\u8D25: " + (e && e.message || e));
  };
  const syncKwsListening = () => {
    if (!rt.api.startKwsListening) return;
    if (kwsEnabled.value && !isRecording.value && !isUiActive()) {
      rt.api.startKwsListening({
        keywords: kwsKeywords.value,
        onWake: handleKwsWake,
        onError: handleKwsError
      });
      kwsListening.value = true;
    } else {
      if (kwsListening.value) {
        if (rt.api.stopKwsListening) rt.api.stopKwsListening();
        kwsListening.value = false;
      }
    }
  };
  const confirmKwsSetting = async () => {
    const kw = kwsKeywords.value || DEFAULT_KEYWORD;
    if (!/^[\u4e00-\u9fff]+$/.test(kw)) {
      notify.warning("\u5524\u9192\u8BCD\u53EA\u652F\u6301\u4E2D\u6587");
      return;
    }
    saveKwsSetting();
    saveTtsSetting();
    if (rt.api.speechKwsValidate) {
      try {
        await rt.api.speechKwsValidate(kw);
        notify.success("\u5524\u9192\u8BCD\u5DF2\u751F\u6548");
      } catch (e) {
        notify.error("\u5524\u9192\u8BCD\u6821\u9A8C\u5931\u8D25: " + (e && e.message || e));
        return;
      }
    }
    if (kwsEnabled.value) syncKwsListening();
    settingsVisible.value = false;
  };
  watch3(kwsEnabled, () => syncKwsListening());
  const isRecording = ref6(false);
  const partialText = ref6("");
  const vadRecorder = rt.api.createVadRecorder ? rt.api.createVadRecorder({
    onPartial: (t) => {
      partialText.value = t;
    },
    onFinal: (t) => {
      finalizeVoice(t);
    },
    onError: (e) => {
      notify.error("\u8BED\u97F3\u8BC6\u522B\u5931\u8D25: " + (e && e.message || e));
    }
  }) : {
    start: async () => {
      throw new Error("\u5F53\u524D\u73AF\u5883\u672A\u63A5\u5165\u8BED\u97F3\u8BC6\u522B\u80FD\u529B");
    },
    stop: () => {
    },
    cancel: () => {
    }
  };
  const startRecording = async () => {
    if (isRecordBlocked() || isRecording.value) return;
    partialText.value = "";
    try {
      vadRecorder.cancel();
    } catch (e) {
    }
    isRecording.value = true;
    try {
      await vadRecorder.start();
    } catch (e) {
      isRecording.value = false;
      notify.error("\u65E0\u6CD5\u8BBF\u95EE\u9EA6\u514B\u98CE: " + (e && e.message || e));
    }
  };
  const stopRecording = () => {
    if (!isRecording.value) return;
    isRecording.value = false;
    partialText.value = "";
    try {
      vadRecorder.stop();
    } catch (e) {
    }
  };
  const cancelRecording = () => {
    if (isRecording.value) vadRecorder.cancel();
    isRecording.value = false;
    partialText.value = "";
    if (kwsEnabled.value) syncKwsListening();
  };
  const finalizeVoice = (text) => {
    isRecording.value = false;
    const fromWake = voiceFromWake.value;
    voiceFromWake.value = false;
    partialText.value = "";
    if (text && text.trim()) {
      if (onVoiceResult) onVoiceResult(text, fromWake);
    } else {
      notify.warning("\u672A\u8BC6\u522B\u5230\u8BED\u97F3\u5185\u5BB9");
    }
    if (kwsEnabled.value) syncKwsListening();
  };
  const speakMessage = async (messageId, text) => {
    if (!rt.tts) {
      notify.error("\u5F53\u524D\u73AF\u5883\u672A\u63A5\u5165\u8BED\u97F3\u5408\u6210\u80FD\u529B");
      return;
    }
    try {
      await rt.tts.toggle(messageId, (text || "").trim(), {
        engine: ttsEngine.value,
        voice: ttsVoice.value
      });
    } catch (e) {
      notify.error("\u8BED\u97F3\u5408\u6210\u5931\u8D25: " + (e && e.message || e));
    }
  };
  onMounted(() => {
    if (!mountReady()) return;
    loadSpeechConfig();
    syncKwsListening();
  });
  onBeforeUnmount(() => {
    if (rt.api.stopKwsListening) rt.api.stopKwsListening();
  });
  return {
    // 唤醒设置
    kwsEnabled,
    kwsKeywords,
    kwsListening,
    settingsVisible,
    saveKwsSetting,
    loadSpeechConfig,
    syncKwsListening,
    confirmKwsSetting,
    // 录音
    isRecording,
    partialText,
    voiceFromWake,
    vadRecorder,
    startRecording,
    stopRecording,
    cancelRecording,
    // TTS
    tts: rt.tts,
    ttsEngine,
    ttsVoice,
    edgeTtsVoices,
    saveTtsSetting,
    speakMessage
  };
}

// core/src/vue/useTtsHighlight.ts
import { unref, onMounted as onMounted2, onBeforeUnmount as onBeforeUnmount2, watch as watch4 } from "vue";
var HIGHLIGHT_NAME = "tts-sentence";
var supported = typeof CSS !== "undefined" && !!CSS.highlights && typeof globalThis.Highlight !== "undefined";
var styleInjected = false;
function injectStyle() {
  if (styleInjected || !supported) return;
  styleInjected = true;
  const style = document.createElement("style");
  style.setAttribute("data-tts-highlight", "");
  style.textContent = `::highlight(${HIGHLIGHT_NAME}){background-color:rgba(64,158,255,.28);color:inherit;}`;
  document.head.appendChild(style);
}
function keepChar(ch) {
  return /[0-9a-zA-Z\u4e00-\u9fa5\u3040-\u30ff\uac00-\ud7af]/.test(ch);
}
function buildIndex(root) {
  const positions = [];
  let norm = "";
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
  let node = walker.nextNode();
  while (node) {
    const text = node.nodeValue || "";
    for (let i = 0; i < text.length; i++) {
      if (keepChar(text[i])) {
        norm += text[i];
        positions.push({ node, offset: i });
      }
    }
    node = walker.nextNode();
  }
  return { norm, positions };
}
function normalize(text) {
  let out = "";
  const s = text || "";
  for (let i = 0; i < s.length; i++) {
    if (keepChar(s[i])) out += s[i];
  }
  return out;
}
function clearTtsHighlight() {
  if (!supported) return;
  CSS.highlights.delete(HIGHLIGHT_NAME);
}
function resolve(x) {
  return typeof x === "function" ? x() : unref(x);
}
function useTtsHighlight(containerRef, messageIdRef) {
  const rt = getAiRuntime();
  const ttsStore = rt.tts;
  let cachedIndex = null;
  let cachedMessageId = "";
  let cachedRoot = null;
  let searchFrom = 0;
  function resetCache() {
    cachedIndex = null;
    cachedMessageId = "";
    cachedRoot = null;
    searchFrom = 0;
  }
  function apply() {
    if (!supported || !ttsStore) return;
    const mid = resolve(messageIdRef);
    if (!mid || ttsStore.messageId !== mid) return;
    if (!ttsStore.active) {
      clearTtsHighlight();
      resetCache();
      return;
    }
    const seg = normalize(ttsStore.segText);
    const root = resolve(containerRef);
    if (!root || !seg) {
      clearTtsHighlight();
      return;
    }
    const stale = cachedIndex && (cachedRoot !== root || !cachedIndex.positions.length || !cachedIndex.positions[0].node.isConnected);
    if (!cachedIndex || cachedMessageId !== mid || stale) {
      if (cachedMessageId !== mid) searchFrom = 0;
      cachedIndex = buildIndex(root);
      cachedMessageId = mid;
      cachedRoot = root;
    }
    const { norm, positions } = cachedIndex;
    let idx = norm.indexOf(seg, searchFrom);
    if (idx === -1) idx = norm.indexOf(seg);
    if (idx === -1) {
      clearTtsHighlight();
      return;
    }
    const startPos = positions[idx];
    const endPos = positions[idx + seg.length - 1];
    if (!startPos || !endPos) {
      clearTtsHighlight();
      return;
    }
    searchFrom = idx + seg.length;
    try {
      const range = document.createRange();
      range.setStart(startPos.node, startPos.offset);
      range.setEnd(endPos.node, endPos.offset + 1);
      const HighlightCtor = globalThis.Highlight;
      CSS.highlights.set(HIGHLIGHT_NAME, new HighlightCtor(range));
    } catch (e) {
      clearTtsHighlight();
    }
  }
  onMounted2(() => {
    injectStyle();
    apply();
  });
  watch4(
    () => ttsStore ? [ttsStore.messageId, ttsStore.segText, ttsStore.phase].join("|") : "",
    () => {
      if (!ttsStore) return;
      const mid = resolve(messageIdRef);
      if (ttsStore.messageId !== mid) {
        if (!ttsStore.active) clearTtsHighlight();
        resetCache();
        return;
      }
      apply();
    }
  );
  onBeforeUnmount2(() => {
    if (ttsStore && ttsStore.messageId === resolve(messageIdRef)) clearTtsHighlight();
    resetCache();
  });
  return { clearTtsHighlight };
}

// core/src/vue/useContextMeter.ts
import { ref as ref7 } from "vue";

// core/src/ai/contextMeter.ts
var DEFAULT_LEVELS = { warning: 0.8, critical: 0.95 };
function resolveLevel(ratio, levels = DEFAULT_LEVELS) {
  if (!Number.isFinite(ratio) || ratio <= 0) return "safe";
  if (ratio >= levels.critical) return "critical";
  if (ratio >= levels.warning) return "warning";
  if (ratio >= 0.5) return "normal";
  return "safe";
}
function formatTokens(value) {
  const n = Number.isFinite(value) ? Math.max(Math.round(value), 0) : 0;
  if (n < 1e3) return String(n);
  if (n < 1e6) return `${(n / 1e3).toFixed(1)}K`;
  return `${(n / 1e6).toFixed(1)}M`;
}
function describeLevel(level) {
  switch (level) {
    case "critical":
      return "\u4E0A\u4E0B\u6587\u5DF2\u63A5\u8FD1\u4E0A\u9650\uFF0C\u5EFA\u8BAE\u7ACB\u5373\u538B\u7F29";
    case "warning":
      return "\u4E0A\u4E0B\u6587\u5FEB\u6EE1\u4E86\uFF0C\u53EF\u624B\u52A8\u538B\u7F29\u65E9\u671F\u5BF9\u8BDD";
    case "normal":
      return "\u4E0A\u4E0B\u6587\u4F7F\u7528\u4E2D";
    default:
      return "\u4E0A\u4E0B\u6587\u5145\u88D5";
  }
}
function fetchTransport(options) {
  const base = options.baseUrl ?? "";
  const headers = () => ({
    "Content-Type": "application/json",
    ...options.headers ? options.headers() : {}
  });
  return {
    async get(path) {
      const res = await fetch(base + path, { method: "GET", headers: headers() });
      return res.json();
    },
    async post(path) {
      const res = await fetch(base + path, { method: "POST", headers: headers(), body: "{}" });
      return res.json();
    }
  };
}
function unwrap(raw) {
  const envelope = raw ?? {};
  if (typeof envelope.code === "number" && envelope.code !== 200) {
    return { data: null, msg: envelope.msg || "\u8BF7\u6C42\u5931\u8D25" };
  }
  return { data: envelope.data ?? raw ?? null, msg: envelope.msg || "" };
}
function createContextMeter(options) {
  const transport = options.transport ?? fetchTransport(options);
  const levels = { ...DEFAULT_LEVELS, ...options.thresholds ?? {} };
  const listeners = /* @__PURE__ */ new Set();
  let state = {
    usage: null,
    level: "safe",
    percent: 0,
    loading: false,
    compacting: false,
    error: null,
    notice: null
  };
  function emit(next) {
    state = next;
    listeners.forEach((listener) => listener(state));
    if (options.onChange) options.onChange(state);
  }
  function patch(partial) {
    emit({ ...state, ...partial });
  }
  function applyUsage(usage) {
    const ratio = Number.isFinite(usage.ratio) ? usage.ratio : usage.usable > 0 ? usage.used / usage.usable : 0;
    patch({
      usage,
      level: resolveLevel(ratio, levels),
      percent: Math.round(Math.min(Math.max(ratio, 0), 1) * 100),
      loading: false,
      error: null
    });
  }
  async function refresh() {
    const chatId = options.getChatId();
    if (chatId === null || chatId === void 0 || chatId === "") {
      patch({ usage: null, level: "safe", percent: 0, loading: false, error: null });
      return null;
    }
    patch({ loading: true });
    try {
      const raw = await transport.get(`/v1/chat/${chatId}/context-usage`);
      const { data, msg } = unwrap(raw);
      if (!data) {
        patch({ loading: false, error: msg || "\u8BFB\u53D6\u4E0A\u4E0B\u6587\u5360\u7528\u5931\u8D25" });
        return null;
      }
      applyUsage(data);
      return data;
    } catch (error) {
      patch({ loading: false, error: error.message || "\u7F51\u7EDC\u5F02\u5E38" });
      return null;
    }
  }
  async function compact() {
    const chatId = options.getChatId();
    if (chatId === null || chatId === void 0 || chatId === "") return null;
    patch({ compacting: true, notice: null, error: null });
    try {
      const raw = await transport.post(`/v1/chat/${chatId}/compact`);
      const { data, msg } = unwrap(raw);
      if (!data || !data.compressedRounds) {
        patch({ compacting: false, notice: data?.message || msg || "\u6CA1\u6709\u53EF\u538B\u7F29\u7684\u5185\u5BB9\u4E86" });
        return null;
      }
      patch({ compacting: false, notice: data.message });
      await refresh();
      return data;
    } catch (error) {
      patch({ compacting: false, error: error.message || "\u538B\u7F29\u5931\u8D25" });
      return null;
    }
  }
  return {
    getState: () => ({ ...state }),
    subscribe(listener) {
      listeners.add(listener);
      listener(state);
      return () => listeners.delete(listener);
    },
    refresh,
    compact
  };
}

// core/src/vue/useContextMeter.ts
function useContextMeter(options) {
  const meter = createContextMeter({
    getChatId: options.getChatId,
    baseUrl: options.baseUrl,
    headers: options.headers,
    transport: options.transport
  });
  const usage = ref7(null);
  const level = ref7("safe");
  const percent = ref7(0);
  const loading = ref7(false);
  const compacting = ref7(false);
  const notice = ref7(null);
  const error = ref7(null);
  const usageText = ref7("");
  const levelText = ref7(describeLevel("safe"));
  const unsubscribe = meter.subscribe((state) => {
    usage.value = state.usage;
    level.value = state.level;
    percent.value = state.percent;
    loading.value = state.loading;
    compacting.value = state.compacting;
    notice.value = state.notice;
    error.value = state.error;
    usageText.value = state.usage ? `${formatTokens(state.usage.used)} / ${formatTokens(state.usage.usable)}` : "";
    levelText.value = describeLevel(resolveLevel(state.usage?.ratio ?? 0));
  });
  return {
    usage,
    level,
    percent,
    loading,
    compacting,
    notice,
    error,
    usageText,
    levelText,
    refresh: meter.refresh,
    compact: meter.compact,
    dispose: unsubscribe
  };
}
export {
  CHAT_CACHE_KEYS,
  clearTtsHighlight,
  configureAiRuntime,
  getAiRuntime,
  renderMessageHtml,
  resetAiRuntime,
  rtGet,
  rtRemove,
  rtSet,
  useChatAgent,
  useChatFaq,
  useChatHistory,
  useChatRender,
  useChatStream,
  useContextMeter,
  useTtsHighlight,
  useVoiceChat
};
